/**
 * AgentDocumentsHub — Agent-facing document submission surface.
 *
 * Route: /agent-portal/documents
 * Brand: AIAgentPortal (distinct from AIBrokerPortal and AISearchPortal)
 *
 * Business role in the workflow:
 *   1. Broker uploads source documents in Broker Intelligence → Documents tab
 *   2. Broker enriches AI analysis in Broker Intelligence → AI Review tab
 *      so the Broker Chat Bot's RAG can retrieve structured metadata
 *   3. Broker configures the document checklist via /broker-checklist
 *   4. AGENT (this page) sees the checklist for each of their transactions,
 *      uploads required files, and submits them to the broker for review
 *   5. Broker sees the submissions in Broker Intelligence → Compliance tab
 *      (doc_review incident type) and in → AI Review → Uploaded Documents
 *   6. Broker approves / requests changes — agent sees the status + notes
 *      here and can resubmit a revised version
 *
 * This closes the compliance workflow loop. Without this page, brokers have
 * no pipeline of agent-submitted documents to review.
 *
 * Rebuild of: components/agent-portal/EnhancedAgentDashboard.tsx +
 *             components/agent-portal/TransactionDashboard.tsx +
 *             components/agent-portal/DocumentChecklistWithUploads.tsx
 *
 * Reuses shared primitives from pages/broker-intelligence/shared/ — same
 * HubShell, SplitPanel, ListItem, ProfileHeader, DetailTabs, FilterPills,
 * SearchBar, navy+gold palette.
 *
 * Backend (all endpoints exist, zero new backend work):
 *   GET  /transactions                            → agent's transaction list
 *   GET  /transactions/metrics                    → agent KPIs
 *   GET  /transactions/:id                        → transaction detail
 *   GET  /broker-checklist/:brokerageId/documents → required docs per stage
 *   GET  /broker-review/documents/:transactionId  → broker review status
 *   POST /transaction-documents/upload            → S3 upload (multipart)
 *   POST /transactions/:id/documents              → register document
 *   PUT  /documents/:id/replace                   → resubmit revised version
 *
 * Dropped from the legacy UX per KOPS-465:
 *   - PDFFillerEditor (product retired)
 *   - Documenso integrations (product retired)
 *   - Anvil prefill (deferred until product team confirms status)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faFolderOpen,
  faLayerGroup,
  faHome,
  faUsers,
  faClockRotateLeft,
  faShieldAlt,
  faComments,
  faCheckCircle,
  faTriangleExclamation,
  faCircleExclamation,
  faCloudArrowUp,
  faRotate,
  faCalendarAlt,
  faDollarSign,
  faClipboardList,
  faPaperPlane,
  faBullhorn,
  faEye,
  faBuilding,
  faHandHoldingDollar,
  faForward,
  faLock,
  faCircleInfo,
  faPen,
  faWandMagicSparkles,
  faClipboardCheck,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

// Legacy modals — reused as-is during the 1:1 feature port. They keep
// their Bootstrap skin for now; they will be re-skinned to navy+gold
// after parity is reached, so functionality isn't broken mid-migration.
import EditTransactionModal from '../../components/agent-portal/EditTransactionModal';
import MakeOfferModal from '../../components/agent-portal/MakeOfferModal';

import { isBrokerOrAdmin, isInternalEmployeeFlex } from '../../utils/permissions';

import axiosInstance from '../../helpers/api';
import HubShell, { HubMetric, GoldButton, OutlineButton } from '../broker-intelligence/shared/HubShell';
import DealDashboard from '../../components/deals/DealDashboard';
import DealAnalysis from '../../components/deals/DealAnalysis';

// KOPS-512 — Ask the Broker RAG chat + interoffice team messaging
import AskTheBrokerTab from './AskTheBrokerTab';
import InterofficeChatTab from './InterofficeChatTab';

// Existing working transaction components (1:1 from agent-portal)
import TransactionDashboard from '../../components/agent-portal/TransactionDashboard';
import TransactionDetails from '../../components/agent-portal/TransactionDetails';
import SplitPanel from '../broker-intelligence/shared/SplitPanel';
import SearchBar from '../broker-intelligence/shared/SearchBar';
import FilterPills from '../broker-intelligence/shared/FilterPills';
import ListItem from '../broker-intelligence/shared/ListItem';
import ProfileHeader from '../broker-intelligence/shared/ProfileHeader';
import DetailTabs from '../broker-intelligence/shared/DetailTabs';
import { colors, fonts } from '../broker-intelligence/shared/colors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Transaction {
  id: string;
  file_number?: string;
  type?: string;
  stage?: string;
  client_name?: string;
  client_email?: string;
  brokerage_id?: string;
  compliance_status?: string;
  compliance_score?: number | string;
  created_at?: string;
  updated_at?: string;
  closed_at?: string;
  documents_count?: number;
  property?: { address?: string; price?: number | string; city?: string; state?: string } | null;
  property_data?: any;
}

interface TransactionDetail extends Transaction {
  // Legacy-compat fields consumed by OverviewPane, PartiesPane, etc.
  agent_id?: string;
  agent_role?: string;
  client_id?: string;
  client_phone?: string;
  notes?: string;
  estimated_closing_date?: string;
  offers?: any[];
  submissions?: any[];
  activities?: any[];
  commission_override_rate?: number | string;
  commission_override_misc_fees?: any;
  commission_override_special_instructions?: string;
  commission_override_is_table_funding?: boolean;
  commission_override_id?: string;
  parties?: Array<{
    id: string;
    full_name?: string;
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
    role_description?: string;
    party_type?: string;
    is_primary?: boolean;
    added_at?: string;
    user_id?: string;
    contact_id?: string;
  }>;
  documents?: Array<{
    id: string;
    document_name?: string;
    document_filename?: string;
    original_filename?: string;
    filename?: string;
    document_role?: string;
    document_type?: string;
    status?: string;
    review_status?: string;
    broker_notes?: string;
    created_at?: string;
    updated_at?: string;
    s3_url?: string;
    view_url?: string;
    original_s3_url?: string;
    ai_analysis?: any;
  }>;
}

interface RequiredDocument {
  id: string;
  name: string;
  document_type?: string;
  type?: string;
  transaction_stages?: string[];
  requirement_level?: 'required' | 'optional' | 'conditional';
  description?: string;
  timing?: string;
  ai_analysis?: any;
  s3_url?: string;
}

// Enhanced document — what /broker-review/documents/:txnId returns.
// Carries both the uploaded file info (filename/original_filename/s3) AND
// the broker review status fields + exemption info, so a single fetch
// covers both the checklist matching and the review status panel.
interface BrokerReview {
  id: string;
  document_id?: string;
  filename?: string;
  original_filename?: string;
  name?: string;
  description?: string;
  document_type?: string;
  s3_url?: string;
  s3_key?: string;
  created_at?: string;
  added_at?: string;
  status?: string;
  broker_review_status?: string | null;
  broker_compliance_score?: number | string | null;
  broker_reviewed_at?: string | null;
  broker_review_notes?: string | null;
  // Legacy-friendly aliases (old panes referenced these)
  review_status?: string;
  compliance_score?: number;
  broker_notes?: string;
  reviewed_at?: string;
  reviewer_name?: string;
  document_name?: string;
  // Exemption fields — populated when the agent/broker exempts a required
  // doc from the checklist instead of uploading it.
  exemption_id?: string | null;
  exemption_reason?: string | null;
  exempted_by?: string | null;
}

interface AgentMetrics {
  active?: number;
  pendingCompliance?: number;
  documentsThisWeek?: number;
  complianceScore?: number;
  // Landing-dashboard KPI fields (ported from EnhancedAgentDashboard).
  // Commission / monthly-goal fields are intentionally NOT in this
  // interface — they're not shown on the landing and don't have a
  // backend source yet (see KOPS-465 commit msg for details).
  closedThisMonth?: number;
  totalPipelineValue?: number;
  averageDaysToClose?: number | null;
}

interface AgentInfo {
  name: string;
  title: string;
  licenseNumber: string;
}

interface BrokerageInfo {
  brokerageName: string;
  brokerName: string;
  agentCount: number;
  city: string;
  state: string;
}

// Aggregated broker review item — one per reviewed document, with a
// pointer back to its parent transaction so the card can deep-link.
interface LandingBrokerReview {
  id: string;
  transaction_id: string;
  document_name: string;
  status: 'approved' | 'needs_revision' | 'pending';
  submitted_date?: string;
  reviewed_date?: string;
  score?: number | string;
  broker_notes?: string;
  property_address?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STAGE_FILTER_OPTIONS = [
  { key: 'all',            label: 'All' },
  { key: 'pre_contract',   label: 'New' },
  { key: 'showing',        label: 'Showing' },
  { key: 'under_contract', label: 'Under Contract' },
  { key: 'closing',        label: 'Closing' },
  { key: 'closed',         label: 'Closed' },
];

const STAGE_COLORS: Record<string, string> = {
  pre_contract:   colors.muted,
  showing:        colors.blue,
  under_contract: colors.gold2,
  closing:        colors.gold,
  closed:         colors.green,
};

const STAGE_LABELS: Record<string, string> = {
  pre_contract:   'New',
  showing:        'Showing',
  under_contract: 'Under Contract',
  closing:        'Closing',
  closed:         'Closed',
};

const BASE_DETAIL_TABS = [
  { key: 'overview',  label: 'Overview',  icon: faCircleInfo },
  { key: 'documents', label: 'Documents', icon: faFolderOpen },
  { key: 'parties',   label: 'Parties',   icon: faUsers },
  { key: 'timeline',  label: 'Timeline',  icon: faClockRotateLeft },
];

// Closing sub-tab — appended dynamically when:
//   (a) current user is broker/admin/internal, AND
//   (b) selected transaction.stage is 'under_contract' or 'closing'
// See TransactionDetailPanel props for the reveal logic.
const CLOSING_TAB = { key: 'closing', label: 'Closing', icon: faLock };

const HUB_TABS = [
  { key: 'documents',        label: 'Documents',        icon: faFolderOpen },
  { key: 'transactions',     label: 'Transactions',     icon: faFileContract },
  { key: 'investor-deals',   label: 'Investor Deals',   icon: faHandHoldingDollar },
  { key: 'ask-the-broker',   label: 'Ask the Broker',   icon: faShieldAlt },
  { key: 'interoffice-chat', label: 'Interoffice Chat', icon: faComments },
];

// Stage groups for the checklist pane — matches DocumentChecklistWithUploads.
const CHECKLIST_STAGES: Array<{ key: string; label: string; description: string }> = [
  { key: 'pre_contract',   label: 'Pre-Contract',   description: 'Before the offer is made and accepted' },
  { key: 'under_contract', label: 'Under Contract', description: 'Between acceptance and closing' },
  { key: 'closing',        label: 'Closing',        description: 'Final closing paperwork' },
  { key: 'post_closing',   label: 'Post-Closing',   description: 'After the transaction closes' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AgentDocumentsHub: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [metrics, setMetrics] = useState<AgentMetrics>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const [selected, setSelected] = useState<Transaction | null>(null);
  const [detail, setDetail] = useState<TransactionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('overview');

  const [requiredDocs, setRequiredDocs] = useState<RequiredDocument[]>([]);
  const [brokerReviews, setBrokerReviews] = useState<BrokerReview[]>([]);

  const [actionAlert, setActionAlert] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  // Brokerage context — needed to fetch the checklist
  const [brokerageId, setBrokerageId] = useState<string>('');

  // Landing dashboard state (port of EnhancedAgentDashboard) — shown
  // in the right panel when no transaction is selected.
  const [agentInfo, setAgentInfo] = useState<AgentInfo>({
    name: '',
    title: 'Licensed Real Estate Agent',
    licenseNumber: 'Contact Admin for License Info',
  });
  const [brokerageInfo, setBrokerageInfo] = useState<BrokerageInfo>({
    brokerageName: '',
    brokerName: '',
    agentCount: 0,
    city: '',
    state: '',
  });
  const [landingReviews, setLandingReviews] = useState<LandingBrokerReview[]>([]);

  // New Transaction modal state — minimum fields, auto-opens the new
  // transaction's Overview for full editing after creation.
  const [showNewTxModal, setShowNewTxModal] = useState(false);

  // URL-driven hub tab state. The URL is the source of truth so browser
  // back/forward works naturally and users can bookmark/share the tab
  // they care about. Three routes all render this same component:
  //   /agent-portal/documents                    → Documents tab
  //   /agent-portal/investor-deals               → Investor Deals tab (list)
  //   /agent-portal/investor-deals/:dealId       → Investor Deals tab (deal drill-down)
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams<{ dealId?: string }>();

  const activeHubTab: 'documents' | 'transactions' | 'investor-deals' | 'ask-the-broker' | 'interoffice-chat' = useMemo(() => {
    if (location.pathname.startsWith('/agent-portal/ask-the-broker')) return 'ask-the-broker';
    if (location.pathname.startsWith('/agent-portal/interoffice-chat')) return 'interoffice-chat';
    if (location.pathname.startsWith('/agent-portal/investor-deals')) return 'investor-deals';
    if (location.pathname.startsWith('/agent-portal/transactions')) return 'transactions';
    return 'documents';
  }, [location.pathname]);

  // Transaction drill-down ID from URL (e.g. /agent-portal/transactions/:id)
  const selectedTransactionId: string | null = useMemo(() => {
    const match = location.pathname.match(/\/agent-portal\/transactions\/([^/]+)/);
    if (match && match[1] !== 'new') return match[1];
    return null;
  }, [location.pathname]);

  // Selected deal id comes from the URL too. :dealId in the route param
  // drives DealAnalysis inline rendering; empty means show the list.
  const selectedDealId: number | null = useMemo(() => {
    if (!routeParams.dealId) return null;
    const n = parseInt(routeParams.dealId, 10);
    return Number.isFinite(n) ? n : null;
  }, [routeParams.dealId]);

  // Tab click → push URL. React Router updates location which updates
  // the derived activeHubTab. Clears any drilled-in deal when switching
  // away from the investor-deals tab.
  const handleHubTabChange = useCallback(
    (key: string) => {
      if (key === 'investor-deals') {
        navigate('/agent-portal/investor-deals');
      } else if (key === 'transactions') {
        navigate('/agent-portal/transactions');
      } else if (key === 'ask-the-broker') {
        navigate('/agent-portal/ask-the-broker');
      } else if (key === 'interoffice-chat') {
        navigate('/agent-portal/interoffice-chat');
      } else {
        navigate('/agent-portal/documents');
      }
    },
    [navigate]
  );

  // Role detection — used to reveal the Closing sub-tab + the
  // "Broker Intelligence" cross-link button only to broker/admin users.
  // Reads from the same three sources as BrokerIntelligenceHub's access
  // guard (localStorage user, user_info, and the JWT payload).
  const currentUser = useMemo(() => {
    const merged: any = {};
    try {
      const stored = localStorage.getItem('user');
      if (stored) Object.assign(merged, JSON.parse(stored));
    } catch { /* ignore */ }
    try {
      const stored = localStorage.getItem('user_info');
      if (stored) Object.assign(merged, JSON.parse(stored));
    } catch { /* ignore */ }
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Merge ALL JWT fields into the user context (not just role).
        // The Kairos JWT carries `name: "Michael Boykin"`, `email`, and
        // sometimes `first_name` — the welcome header needs those when
        // localStorage.user was written by a legacy login flow that only
        // stored a partial record.
        if (payload.role && !merged.role) merged.role = payload.role;
        if (payload.user_role && !merged.user_role) merged.user_role = payload.user_role;
        if (payload.name && !merged.name) merged.name = payload.name;
        if (payload.email && !merged.email) merged.email = payload.email;
        if (payload.first_name && !merged.first_name) merged.first_name = payload.first_name;
        if (payload.last_name && !merged.last_name) merged.last_name = payload.last_name;
        if (payload.user_first_name && !merged.user_first_name) merged.user_first_name = payload.user_first_name;
        if (payload.user_last_name && !merged.user_last_name) merged.user_last_name = payload.user_last_name;
      }
    } catch { /* ignore */ }
    return merged;
  }, []);

  const isBrokerRole = useMemo(
    () => isBrokerOrAdmin(currentUser) || isInternalEmployeeFlex(currentUser),
    [currentUser]
  );

  // ---- Fetch brokerage context + populate landing header -----------------
  // Pulls /broker/company-profile to set the brokerage id (used by the
  // checklist) and also extracts the display fields the welcome header
  // needs: name, city, state. Falls back gracefully when fields are missing.
  useEffect(() => {
    const cached = localStorage.getItem('brokerageId') || '';
    if (cached) setBrokerageId(cached);

    (async () => {
      try {
        const res = await axiosInstance.get('/broker/company-profile');
        if (res.data?.success && res.data?.data) {
          const data = res.data.data;
          if (data.brokerage_id) {
            setBrokerageId(data.brokerage_id);
            localStorage.setItem('brokerageId', data.brokerage_id);
          }
          setBrokerageInfo((prev) => ({
            ...prev,
            brokerageName: data.company_name || prev.brokerageName || 'Your Brokerage',
            brokerName: data.broker_name || prev.brokerName || '',
            agentCount: data.agent_count ?? prev.agentCount,
            city: extractCityFromAddress(data.company_address) || prev.city,
            state: extractStateFromAddress(data.company_address) || prev.state,
          }));
        }
      } catch {
        /* best effort — if there's no brokerage the checklist just won't populate */
      }
    })();
  }, []);

  // ---- Populate agent info from merged user context + /agent/profile ----
  // Pulls from `currentUser` which already merges:
  //   - localStorage.user       (Users table columns: user_first_name, etc.)
  //   - localStorage.user_info  (legacy key some flows set)
  //   - JWT payload             (has `name: "Michael Boykin"` directly)
  // This triple fallback is why the welcome header on some accounts was
  // stuck on "Real Estate Agent": localStorage.user lacked first/last
  // names but the JWT had the full name all along.
  useEffect(() => {
    const u: any = currentUser || {};

    // First name only — the welcome greeting is personal ("Welcome back,
    // Michael") and using the full name feels clinical. Try every shape
    // we've seen in the wild:
    //   1. user_first_name  (kairosiq Users table)
    //   2. first_name       (alternate schema)
    //   3. First word of full `name` / `full_name` (JWT payload, SSO)
    //   4. preferred_name / nickname if present
    // Deliberately NOT falling back to email local-part — "boykingroup"
    // or "john.smith" as a greeting is worse than no name at all.
    const firstName =
      u.preferred_name ||
      u.nickname ||
      u.user_first_name ||
      u.first_name ||
      (typeof u.name === 'string' ? u.name.trim().split(/\s+/)[0] : '') ||
      (typeof u.full_name === 'string' ? u.full_name.trim().split(/\s+/)[0] : '') ||
      '';

    const licenseDisplay =
      u.license_number && u.license_state
        ? `${u.license_state}-${u.license_number}`
        : u.license_number || 'Contact Admin for License Info';

    setAgentInfo((prev) => ({
      ...prev,
      name: firstName,
      title: u.title || 'Licensed Real Estate Agent',
      licenseNumber: licenseDisplay,
    }));

    // Secondary fetch: if license is missing, try the dedicated profile
    // endpoint (same as legacy dashboard).
    if (!u.license_number) {
      (async () => {
        try {
          const res = await axiosInstance.get('/agent/profile');
          if (res.data?.license_number) {
            const license = res.data.license_state
              ? `${res.data.license_state}-${res.data.license_number}`
              : res.data.license_number;
            setAgentInfo((prev) => ({ ...prev, licenseNumber: license }));
          }
        } catch { /* best effort */ }
      })();
    }
  }, [currentUser]);

  // ---- Fetch transactions + metrics ---------------------------------------
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, metricsRes] = await Promise.all([
        axiosInstance.get('/transactions', {
          // Force agent scope — the Agent Portal always shows the viewer's own
          // deals, even when the viewer is a broker/admin. A separate Broker
          // Portal (future work) will show the team-wide pipeline.
          params: {
            scope: 'agent',
            ...(stageFilter !== 'all' ? { stage: stageFilter } : {}),
          },
        }),
        axiosInstance.get('/transactions/metrics').catch(() => ({ data: {} })),
      ]);
      if (listRes.data?.success) {
        // Backend returns property data under `property_data` (JSON column)
        // not `property`. Normalize so downstream reads of `t.property?.address`
        // work regardless of the source shape. Also pick up the
        // `client_display_name` alias for client_name.
        const rawList: any[] = listRes.data.transactions || [];
        const normalized: Transaction[] = rawList.map((t) => {
          const pd = t.property_data || t.property || null;
          return {
            ...t,
            client_name: t.client_name || t.client_display_name || undefined,
            property: pd
              ? {
                  address: pd.address,
                  city: pd.city,
                  state: pd.state,
                  price: pd.price,
                }
              : null,
            property_data: t.property_data,
          };
        });
        setTransactions(normalized);
      } else {
        setError(listRes.data?.error || 'Failed to load transactions');
      }
      setMetrics(metricsRes.data?.metrics || metricsRes.data || {});
    } catch (err: any) {
      // Surface the 403 BROKERAGE_REQUIRED error helpfully instead of hiding it
      const code = err?.response?.data?.code;
      const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
      if (code === 'BROKERAGE_REQUIRED' || err?.response?.status === 403) {
        setError(
          "Your account isn't linked to an active brokerage yet. Ask your broker to add you to their team — once they do, sign out and back in to refresh your access."
        );
      } else {
        setError(serverMsg || err?.message || 'Failed to load transactions');
      }
    } finally {
      setLoading(false);
    }
  }, [stageFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // ---- Aggregate broker reviews across top 10 transactions ---------------
  // Ported 1:1 from EnhancedAgentDashboard — walks the most recent 10 deals,
  // pulls /broker-review/documents/:txnId for each, normalizes statuses, and
  // sorts by most-recent activity. Feeds the Recent Broker Reviews card on
  // the landing view. Runs after transactions load and refreshes when the
  // transactions list changes.
  useEffect(() => {
    if (transactions.length === 0) {
      setLandingReviews([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const recent = transactions.slice(0, 10);
      const all: LandingBrokerReview[] = [];
      for (const txn of recent) {
        if (cancelled) return;
        try {
          const res = await axiosInstance.get(`/broker-review/documents/${txn.id}`);
          const docs: any[] = res.data?.documents || [];
          docs
            .filter((d) => d.broker_review_status)
            .forEach((d) => {
              // Map inconsistent backend status values into three buckets,
              // matching the legacy dashboard exactly.
              let status: LandingBrokerReview['status'] = 'pending';
              if (
                d.broker_review_status === 'approved' ||
                d.broker_review_status === 'approve' ||
                d.broker_review_status === 'passed'
              ) {
                status = 'approved';
              } else if (
                d.broker_review_status === 'needs_revision' ||
                d.broker_review_status === 'revision' ||
                d.broker_review_status === 'rejected'
              ) {
                status = 'needs_revision';
              }
              all.push({
                id: d.id,
                transaction_id: txn.id,
                // Prefer the original uploaded filename (what the agent named
                // the file when they saved it, e.g. "Residential Purchase
                // Contract.pdf") over document_type which is just the
                // broker's template slot label (e.g. "purchase_contract").
                document_name:
                  d.original_filename ||
                  d.filename ||
                  d.document_name ||
                  d.document_type ||
                  'Document',
                status,
                submitted_date: d.uploaded_at || d.added_at,
                reviewed_date: d.broker_reviewed_at,
                score: d.broker_compliance_score ?? d.compliance_score,
                broker_notes: d.broker_review_notes || d.broker_notes,
                property_address:
                  txn.property?.address || `${txn.client_name || 'Client'}'s Transaction`,
              });
            });
        } catch {
          /* best effort per txn */
        }
      }
      if (cancelled) return;
      all.sort((a, b) => {
        const aDate = new Date(a.reviewed_date || a.submitted_date || 0).getTime();
        const bDate = new Date(b.reviewed_date || b.submitted_date || 0).getTime();
        return bDate - aDate;
      });
      setLandingReviews(all.slice(0, 20));
    })();
    return () => {
      cancelled = true;
    };
  }, [transactions]);

  // ---- Fetch transaction detail + reviews + checklist when selection changes
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      setBrokerReviews([]);
      return;
    }

    let cancelled = false;

    const loadAll = async () => {
      setDetailLoading(true);
      setDetail(null);

      // Detail
      try {
        const res = await axiosInstance.get(`/transactions/${selected.id}`);
        // eslint-disable-next-line no-console
        console.log('[AgentDocumentsHub] /transactions/:id response', {
          success: res.data?.success,
          documents: (res.data?.transaction?.documents || []).length,
          parties: (res.data?.transaction?.parties || []).length,
          activities: (res.data?.transaction?.activities || []).length,
          firstDoc: res.data?.transaction?.documents?.[0],
          firstParty: res.data?.transaction?.parties?.[0],
        });
        if (!cancelled && res.data?.success) {
          const t: any = res.data.transaction;
          const pd = t.property_data || t.property || null;
          setDetail({
            ...t,
            client_name: t.client_name || t.client_display_name || undefined,
            property: pd
              ? { address: pd.address, city: pd.city, state: pd.state, price: pd.price }
              : null,
            property_data: t.property_data,
          });
        } else if (!cancelled) {
          setDetail(selected as TransactionDetail);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[AgentDocumentsHub] /transactions/:id failed', err);
        if (!cancelled) setDetail(selected as TransactionDetail);
      }

      // Broker review status for this transaction's documents
      try {
        const reviewRes = await axiosInstance.get(`/broker-review/documents/${selected.id}`);
        if (!cancelled && reviewRes.data?.success) {
          setBrokerReviews(reviewRes.data.documents || reviewRes.data.reviews || []);
        }
      } catch {
        if (!cancelled) setBrokerReviews([]);
      }

      if (!cancelled) setDetailLoading(false);
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [selected]);

  // ---- Fetch broker checklist when brokerage AND selected tx are known ----
  // The /broker-checklist/:brokerageId/documents endpoint requires a
  // transaction_type query param — different tx types have different
  // required-doc lists — so we refetch whenever the selected tx changes.
  useEffect(() => {
    if (!brokerageId || !selected?.type) {
      setRequiredDocs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/broker-checklist/${brokerageId}/documents`, {
          params: { transaction_type: selected.type },
        });
        if (cancelled) return;
        if (res.data?.success) {
          setRequiredDocs(res.data.documents || res.data.checklist || []);
        } else if (Array.isArray(res.data)) {
          setRequiredDocs(res.data);
        } else {
          setRequiredDocs([]);
        }
      } catch {
        /* best effort — no checklist just means the agent uses free-form upload */
        if (!cancelled) setRequiredDocs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brokerageId, selected?.type]);

  // ---- Derived: filter + search -------------------------------------------
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        t.file_number?.toLowerCase().includes(q) ||
        t.client_name?.toLowerCase().includes(q) ||
        t.property?.address?.toLowerCase().includes(q)
      );
    });
  }, [transactions, search]);

  // ---- Upload a document to a transaction ---------------------------------
  const handleUpload = async (
    file: File,
    opts: { documentRole?: string; documentName?: string; requiredDocId?: string }
  ) => {
    if (!selected) return;
    setUploadingFor(opts.requiredDocId || opts.documentName || file.name);
    try {
      // 1. Upload to S3 via multipart endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('transaction_id', selected.id);
      if (opts.documentRole) formData.append('document_role', opts.documentRole);
      if (opts.documentName) formData.append('document_name', opts.documentName);
      const uploadRes = await axiosInstance.post('/transaction-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (!uploadRes.data?.success) {
        throw new Error(uploadRes.data?.error || 'Upload failed');
      }

      // 2. Register the document with the transaction so it appears in the
      //    broker's review queue
      await axiosInstance.post(`/transactions/${selected.id}/documents`, {
        document_id: uploadRes.data?.document?.id || uploadRes.data?.id,
        document_name: opts.documentName || file.name,
        document_role: opts.documentRole || 'other',
      });

      setActionAlert(`✅ Uploaded ${file.name} — submitted for broker review`);
      // Refresh the detail panel so the new doc appears immediately
      const refreshRes = await axiosInstance.get(`/transactions/${selected.id}`);
      if (refreshRes.data?.success) setDetail(refreshRes.data.transaction);
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Upload failed'}`);
    } finally {
      setUploadingFor(null);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Replace (resubmit revised version) ---------------------------------
  const handleReplace = async (documentId: string, file: File) => {
    setUploadingFor(documentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axiosInstance.put(`/documents/${documentId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (res.data?.success !== false) {
        setActionAlert('✅ Revised version submitted — broker will re-review');
        if (selected) {
          const refreshRes = await axiosInstance.get(`/transactions/${selected.id}`);
          if (refreshRes.data?.success) setDetail(refreshRes.data.transaction);
        }
      } else {
        setActionAlert(`❌ ${res.data?.error || 'Replace failed'}`);
      }
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Replace failed'}`);
    } finally {
      setUploadingFor(null);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Stats strip metrics -------------------------------------------------
  const hubMetrics: HubMetric[] = useMemo(() => {
    const active = metrics.active ?? transactions.filter((t) => t.stage && t.stage !== 'closed').length;
    const pending = metrics.pendingCompliance ?? 0;
    const thisWeek = metrics.documentsThisWeek ?? 0;
    const score = metrics.complianceScore;
    const items: HubMetric[] = [
      { icon: faFileContract, value: active, label: 'Active Deals', color: colors.blue },
      { icon: faClipboardList, value: pending, label: 'Pending Review', color: colors.orange },
      { icon: faCloudArrowUp, value: thisWeek, label: 'This Week', color: colors.gold2 },
    ];
    if (score != null) {
      items.push({
        icon: faCheckCircle,
        value: `${Math.round(Number(score))}%`,
        label: 'Compliance Score',
        color: colors.green,
      });
    }
    return items;
  }, [metrics, transactions]);

  const actions = (
    <>
      {isBrokerRole && (
        <OutlineButton
          onClick={() => (window.location.href = '/broker-intelligence-v2')}
        >
          🏢 Broker Intelligence
        </OutlineButton>
      )}
      <OutlineButton onClick={() => fetchTransactions()}>Refresh</OutlineButton>
      <GoldButton onClick={() => setShowNewTxModal(true)}>
        + New Transaction
      </GoldButton>
    </>
  );

  // ---- Render --------------------------------------------------------------
  return (
    <HubShell
      breadcrumb={
        activeHubTab === 'investor-deals'
          ? 'Agent Portal / Investor Deals'
          : activeHubTab === 'transactions'
          ? 'Agent Portal / Transactions'
          : activeHubTab === 'ask-the-broker'
          ? 'Agent Portal / Ask the Broker'
          : activeHubTab === 'interoffice-chat'
          ? 'Agent Portal / Interoffice Chat'
          : 'Agent Portal / Documents'
      }
      brandMiddle="Agent"
      homeHref="/agent-portal/dashboard"
      metrics={activeHubTab === 'documents' ? hubMetrics : []}
      actions={activeHubTab === 'documents' ? actions : null}
      tabs={HUB_TABS}
      activeTab={activeHubTab}
      onTabChange={handleHubTabChange}
    >
      {actionAlert && (
        <div
          style={{
            background: actionAlert.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: actionAlert.startsWith('✅') ? colors.green : colors.red,
            padding: '8px 24px',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {actionAlert}
        </div>
      )}

      {/* Investor Deals tab — full-width DealDashboard port of the
          /investor-deals surface from app.agentkairos.com. Mike asked
          for this as a dedicated tab for Brandon so agents can access
          the investor workflow without leaving the agent portal. */}
      {activeHubTab === 'ask-the-broker' ? (
        <AskTheBrokerTab />
      ) : activeHubTab === 'interoffice-chat' ? (
        <InterofficeChatTab />
      ) : activeHubTab === 'transactions' ? (
        <div style={{ background: '#fff', padding: 16, overflowY: 'auto', height: '100%' }}>
          {selectedTransactionId ? (
            <TransactionDetails
              transactionId={selectedTransactionId}
              onBack={() => navigate('/agent-portal/transactions')}
              portal="agent"
            />
          ) : (
            <TransactionDashboard />
          )}
        </div>
      ) : activeHubTab === 'investor-deals' ? (
        <div style={{ flex: 1, overflow: 'auto', background: colors.off, padding: '0' }}>
          {selectedDealId ? (
            <DealAnalysis
              dealId={selectedDealId}
              onBack={() => navigate('/agent-portal/investor-deals')}
            />
          ) : (
            <DealDashboard
              onDealClick={(dealId) =>
                navigate(`/agent-portal/investor-deals/${dealId}`)
              }
            />
          )}
        </div>
      ) : (
      <SplitPanel
        left={
          <>
            <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
              <div style={{ marginBottom: '8px' }}>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Search transactions..."
                />
              </div>
              <FilterPills options={STAGE_FILTER_OPTIONS} activeKey={stageFilter} onChange={setStageFilter} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
                  Loading transactions…
                </div>
              ) : error ? (
                <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
              ) : filteredTransactions.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
                  <FontAwesomeIcon icon={faFileContract} style={{ fontSize: '32px', marginBottom: '8px' }} />
                  <div style={{ fontSize: '13px' }}>
                    {transactions.length === 0
                      ? 'No transactions yet. Create your first deal to start uploading documents.'
                      : 'No transactions match current filters'}
                  </div>
                </div>
              ) : (
                filteredTransactions.map((t) => (
                  <ListItem
                    key={t.id}
                    selected={selected?.id === t.id}
                    onClick={() => {
                      setSelected(t);
                      setActiveDetailTab('overview');
                    }}
                    title={t.property?.address || t.client_name || t.file_number || '(no address)'}
                    subtitle={t.client_name ? `Client: ${t.client_name}` : t.type}
                    meta={
                      <span>
                        {t.file_number ? `#${t.file_number} · ` : ''}
                        {formatRelativeTime(t.updated_at || t.created_at)}
                      </span>
                    }
                    rightContent={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                        <StageBadge stage={t.stage} />
                        <ComplianceIcon status={t.compliance_status} />
                      </div>
                    }
                  />
                ))
              )}
            </div>
          </>
        }
        right={
          selected ? (
            <TransactionDetailPanel
              transaction={detail || (selected as TransactionDetail)}
              loading={detailLoading}
              activeTab={activeDetailTab}
              onTabChange={setActiveDetailTab}
              requiredDocs={requiredDocs}
              brokerReviews={brokerReviews}
              uploadingFor={uploadingFor}
              onUpload={handleUpload}
              onReplace={handleReplace}
              isBrokerRole={isBrokerRole}
              onClosingAction={async () => {
                if (!selected) return;
                try {
                  const res = await axiosInstance.get(`/transactions/${selected.id}`);
                  if (res.data?.success) setDetail(res.data.transaction);
                } catch { /* best effort */ }
                fetchTransactions();
              }}
              onClosingAlert={(msg) => {
                setActionAlert(msg);
                setTimeout(() => setActionAlert(null), 4000);
              }}
              onTransactionUpdated={async (msg) => {
                // Refetch the selected transaction + the list + broker reviews
                // so every pane sees the updated values after the Overview
                // form save (type, stage, property data, etc.)
                if (!selected) return;
                try {
                  const res = await axiosInstance.get(`/transactions/${selected.id}`);
                  if (res.data?.success) {
                    const t: any = res.data.transaction;
                    const pd = t.property_data || t.property || null;
                    setDetail({
                      ...t,
                      client_name: t.client_name || t.client_display_name || undefined,
                      property: pd
                        ? { address: pd.address, city: pd.city, state: pd.state, price: pd.price }
                        : null,
                      property_data: t.property_data,
                    });
                  }
                } catch { /* best effort */ }
                fetchTransactions();
                if (msg) {
                  setActionAlert(msg);
                  setTimeout(() => setActionAlert(null), 4000);
                }
              }}
            />
          ) : (
            <LandingDashboard
              agentInfo={agentInfo}
              brokerageInfo={brokerageInfo}
              metrics={metrics}
              transactions={transactions}
              landingReviews={landingReviews}
              onSelectTransaction={(t) => {
                setSelected(t);
                setActiveDetailTab('overview');
              }}
            />
          )
        }
      />
      )}

      {showNewTxModal && (
        <NewTransactionModal
          onClose={() => setShowNewTxModal(false)}
          onCreated={async (newTxId, optimisticTx) => {
            setShowNewTxModal(false);
            setActionAlert('✅ Transaction created');
            setTimeout(() => setActionAlert(null), 4000);

            // 1. Reset the stage filter to 'all' — if the user had a
            //    filter active (e.g., "closing"), a brand-new deal in
            //    pre_contract would be invisible after the refetch.
            setStageFilter('all');

            // 2. Optimistic update: if the POST response included the
            //    transaction object, prepend it to the list immediately
            //    so the left panel updates even if the GET /transactions
            //    has DB-indexing lag.
            if (optimisticTx) {
              const pd = optimisticTx.property_data || optimisticTx.property || null;
              const normalized: Transaction = {
                ...optimisticTx,
                client_name: optimisticTx.client_name || optimisticTx.client_display_name,
                property: pd
                  ? { address: pd.address, city: pd.city, state: pd.state, price: pd.price }
                  : null,
              };
              setTransactions((prev) => {
                // De-dupe in case the optimistic row collides with a later refetch
                if (prev.some((t) => t.id === normalized.id)) return prev;
                return [normalized, ...prev];
              });
              setSelected(normalized);
              setActiveDetailTab('overview');
            }

            // 3. Refetch in the background to get the canonical state.
            //    Small delay lets the backend finish any post-INSERT hooks.
            setTimeout(() => {
              fetchTransactions();
            }, 300);

            // 4. Also fetch the full detail (for documents/parties/etc)
            //    and upgrade the selected row from the optimistic shape
            //    to the authoritative server shape.
            try {
              const res = await axiosInstance.get(`/transactions/${newTxId}`);
              if (res.data?.success) {
                const t: any = res.data.transaction;
                const pd = t.property_data || t.property || null;
                const selectedTx: Transaction = {
                  ...t,
                  client_name: t.client_name || t.client_display_name,
                  property: pd
                    ? { address: pd.address, city: pd.city, state: pd.state, price: pd.price }
                    : null,
                };
                setSelected(selectedTx);
                setActiveDetailTab('overview');
              }
            } catch { /* best effort */ }
          }}
        />
      )}
    </HubShell>
  );
};

// ===========================================================================
// Right panel — transaction detail with 5 sub-tabs
// ===========================================================================

interface TransactionDetailPanelProps {
  transaction: TransactionDetail;
  loading: boolean;
  activeTab: string;
  onTabChange: (k: string) => void;
  requiredDocs: RequiredDocument[];
  brokerReviews: BrokerReview[];
  uploadingFor: string | null;
  onUpload: (file: File, opts: { documentRole?: string; documentName?: string; requiredDocId?: string }) => void;
  onReplace: (documentId: string, file: File) => void;
  isBrokerRole: boolean;
  onClosingAction: () => void;
  onClosingAlert: (msg: string) => void;
  onTransactionUpdated: (msg?: string) => void;
}

const TransactionDetailPanel: React.FC<TransactionDetailPanelProps> = ({
  transaction,
  loading,
  activeTab,
  onTabChange,
  requiredDocs,
  brokerReviews,
  uploadingFor,
  onUpload,
  onReplace,
  isBrokerRole,
  onClosingAction,
  onClosingAlert,
  onTransactionUpdated,
}) => {
  const propAddr = transaction.property?.address;
  const propPrice = transaction.property?.price;
  const parties = transaction.parties || [];
  const documents = transaction.documents || [];

  // Closing sub-tab is broker/admin-only AND only when the transaction
  // is at or near closing. Regular agents never see it; brokers see it
  // when stage is under_contract, closing, or closed.
  const showClosingTab =
    isBrokerRole &&
    ['under_contract', 'closing', 'closed'].includes(transaction.stage || '');
  const detailTabs = showClosingTab ? [...BASE_DETAIL_TABS, CLOSING_TAB] : BASE_DETAIL_TABS;

  return (
    <div>
      <ProfileHeader
        title={propAddr || transaction.client_name || transaction.file_number || 'Transaction'}
        metaItems={[
          ...(transaction.client_name ? [{ icon: faHome, text: transaction.client_name }] : []),
          ...(propPrice
            ? [{ icon: faDollarSign, text: `$${Number(propPrice).toLocaleString()}` }]
            : []),
          ...(transaction.file_number ? [{ text: `File #${transaction.file_number}` }] : []),
          { icon: faCalendarAlt, text: formatRelativeTime(transaction.updated_at || transaction.created_at) },
        ]}
        badges={
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <StageBadge stage={transaction.stage} />
            <ComplianceBadge status={transaction.compliance_status} />
          </div>
        }
      />

      <DetailTabs tabs={detailTabs} activeTab={activeTab} onChange={onTabChange} />

      <div style={{ padding: '0 24px 24px 24px' }}>
        {loading && (
          <div style={{ padding: '20px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
            Loading details…
          </div>
        )}
        {activeTab === 'overview' && (
          <OverviewPane
            transaction={transaction}
            onUpdated={onTransactionUpdated}
          />
        )}
        {activeTab === 'documents' && (
          <DocumentsPane
            transaction={transaction}
            requiredDocs={requiredDocs}
            brokerReviews={brokerReviews}
            uploadingFor={uploadingFor}
            onUpload={onUpload}
            onReplace={onReplace}
            onRefresh={() => onTransactionUpdated()}
          />
        )}
        {activeTab === 'parties' && <PartiesPane parties={parties} />}
        {activeTab === 'timeline' && (
          <TimelinePane transaction={transaction} brokerReviews={brokerReviews} />
        )}
        {activeTab === 'closing' && showClosingTab && (
          <ClosingPane
            transaction={transaction}
            onRefresh={onClosingAction}
            onAlert={onClosingAlert}
          />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview pane — inline-editable form for all transaction metadata.
// Replaces the legacy EditTransactionModal by moving every field onto the
// main surface. PUT /transactions/:id on save. Read-only summary cards at
// the top (progress bar, compliance status, doc counts) + editable form
// below split into 4 sections (Transaction, Client, Property, Details).
// ---------------------------------------------------------------------------

const STAGE_ORDER = ['pre_contract', 'showing', 'under_contract', 'closing', 'completed'];
const STAGE_STEP_LABELS: Record<string, string> = {
  pre_contract: 'Pre-Contract',
  showing: 'Showing',
  under_contract: 'Under Contract',
  closing: 'Closing',
  completed: 'Completed',
};

interface OverviewFormState {
  type: string;
  stage: string;
  agent_role: string;
  client_name: string;
  client_email: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  property_price: string;
  estimated_closing_date: string;
  // Smart checklist fields
  property_type: string;
  year_built: string;
  loan_type: string;
  has_hoa: boolean;
  has_pool: boolean;
  has_solar: boolean;
  solar_ownership: string;
  septic_system: boolean;
  water_source: string;
  flood_zone: string;
  requires_flood_insurance: boolean;
  notes: string;
}

const formatDateInput = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Declared as `function` (not `const`) so it's hoisted — the main
// TransactionDetailPanel above renders <OverviewPane /> and TypeScript
// treats forward const-arrow-function refs as errors.
function OverviewPane({
  transaction,
  onUpdated,
}: {
  transaction: TransactionDetail;
  onUpdated: (msg?: string) => void;
}) {
  // Initialize form from current transaction. Rebuild whenever the
  // selected transaction changes (so clicking into a different deal
  // resets the form cleanly).
  const initialForm: OverviewFormState = useMemo(() => {
    const pd: any = transaction.property_data || {};
    const prop: any = transaction.property || {};
    return {
      type: transaction.type || 'purchase',
      stage: transaction.stage || 'pre_contract',
      agent_role: (transaction as any).agent_role || 'buyer_agent',
      client_name: transaction.client_name || '',
      client_email: transaction.client_email || '',
      property_address: prop.address || pd.address || '',
      property_city: prop.city || pd.city || '',
      property_state: prop.state || pd.state || '',
      property_zip: (prop as any).zip || pd.zip || '',
      property_price: String(prop.price || pd.price || (transaction as any).sale_price || ''),
      estimated_closing_date: formatDateInput(
        (transaction as any).estimated_closing_date || pd.estimated_closing_date
      ),
      property_type: pd.property_type || 'residential',
      year_built: pd.year_built ? String(pd.year_built) : '',
      loan_type: pd.loan_type || 'conventional',
      has_hoa: !!pd.has_hoa,
      has_pool: !!pd.has_pool,
      has_solar: !!pd.has_solar,
      solar_ownership: pd.solar_ownership || 'owned',
      septic_system: !!pd.septic_system,
      water_source: pd.water_source || 'city',
      flood_zone: pd.flood_zone || '',
      requires_flood_insurance: !!pd.requires_flood_insurance,
      notes: (transaction as any).notes || '',
    };
  }, [transaction]);

  const [form, setForm] = useState<OverviewFormState>(initialForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset the form whenever the parent transaction object changes
  // (e.g., after an external refresh or clicking a different deal).
  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const isLocked = transaction.stage === 'completed' || !!transaction.closed_at;
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  const update = <K extends keyof OverviewFormState>(key: K, value: OverviewFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const propertyData = {
        address: form.property_address,
        city: form.property_city,
        state: form.property_state,
        zip: form.property_zip,
        price: form.property_price
          ? parseFloat(String(form.property_price).replace(/,/g, ''))
          : null,
        estimated_closing_date: form.estimated_closing_date,
        property_type: form.property_type,
        year_built: form.year_built ? parseInt(form.year_built, 10) : null,
        loan_type: form.loan_type,
        has_hoa: form.has_hoa,
        has_pool: form.has_pool,
        has_solar: form.has_solar,
        solar_ownership: form.solar_ownership,
        septic_system: form.septic_system,
        water_source: form.water_source,
        flood_zone: form.flood_zone,
        requires_flood_insurance: form.requires_flood_insurance,
      };

      const payload = {
        type: form.type,
        stage: form.stage,
        agent_role: form.agent_role,
        client_name: form.client_name,
        property_data: propertyData,
        notes: form.notes,
      };

      const res = await axiosInstance.put(`/transactions/${transaction.id}`, payload);
      if (res.data?.success) {
        onUpdated('✅ Transaction updated');
      } else {
        setSaveError(res.data?.error || 'Failed to save');
      }
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to update transaction'
      );
    } finally {
      setSaving(false);
    }
  };

  const currentStageIdx = STAGE_ORDER.indexOf(transaction.stage || 'pre_contract');
  const progressPercent =
    currentStageIdx < 0 ? 0 : ((currentStageIdx + 1) / STAGE_ORDER.length) * 100;

  return (
    <div style={{ paddingTop: '16px' }}>
      {/* -------- Transaction Progress Bar -------- */}
      <SectionCard title="Transaction Progress">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <StageBadge stage={transaction.stage} />
          <strong style={{ fontSize: '13px', color: colors.navy }}>
            {Math.round(progressPercent)}%
          </strong>
        </div>
        <div
          style={{
            height: '10px',
            background: `${colors.muted}22`,
            borderRadius: '5px',
            overflow: 'hidden',
            marginBottom: '10px',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${colors.gold}, ${colors.gold2})`,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {STAGE_ORDER.map((s) => (
            <span
              key={s}
              style={{
                color: STAGE_ORDER.indexOf(s) <= currentStageIdx ? colors.gold : colors.muted,
                fontWeight: STAGE_ORDER.indexOf(s) === currentStageIdx ? 700 : 400,
              }}
            >
              {STAGE_STEP_LABELS[s]}
            </span>
          ))}
        </div>
      </SectionCard>

      {/* -------- Read-only summary (metadata + compliance) -------- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <SectionCard title="Transaction Information">
          <SummaryRow label="Type" value={transaction.type || '—'} />
          <SummaryRow label="Stage" value={(transaction.stage || '—').replace(/_/g, ' ')} />
          <SummaryRow
            label="Created"
            value={transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : '—'}
          />
          <SummaryRow
            label="Last Updated"
            value={transaction.updated_at ? new Date(transaction.updated_at).toLocaleDateString() : '—'}
          />
        </SectionCard>
        <SectionCard title="Compliance Status">
          <SummaryRow
            label="Status"
            value={transaction.compliance_status || 'pending'}
            highlight={transaction.compliance_status === 'approved' ? colors.green : colors.orange}
          />
          {transaction.compliance_score != null && (
            <SummaryRow label="Score" value={`${transaction.compliance_score}%`} />
          )}
          {/* KOPS-693: raw doc count removed — the stage-aware Compliance % is the
              meaningful signal; a raw "Documents: N" confused it with required progress. */}
          <SummaryRow label="Submissions" value={String((transaction.submissions || []).length)} />
        </SectionCard>
      </div>

      {isLocked && (
        <div
          style={{
            background: `${colors.blue}11`,
            border: `1px solid ${colors.blue}66`,
            borderLeft: `3px solid ${colors.blue}`,
            borderRadius: '6px',
            padding: '10px 14px',
            fontSize: '12px',
            color: colors.navy,
            marginBottom: '16px',
          }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.blue, marginRight: '6px' }} />
          <strong>Transaction Locked:</strong> This transaction is completed. Editing is disabled.
          Contact your broker for any changes.
        </div>
      )}

      {/* -------- Editable form: Transaction Information -------- */}
      <SectionCard title="Edit — Transaction Information">
        <FormGrid>
          <FormField label="Transaction Type">
            <Select
              value={form.type}
              disabled={isLocked}
              onChange={(v) => update('type', v)}
              options={[
                { value: 'purchase', label: 'Purchase' },
                { value: 'listing', label: 'Listing' },
                { value: 'rental', label: 'Rental' },
                { value: 'referral', label: 'Referral' },
              ]}
            />
          </FormField>
          <FormField label="Stage">
            <Select
              value={form.stage}
              disabled={isLocked}
              onChange={(v) => update('stage', v)}
              options={[
                { value: 'pre_contract', label: 'Pre-Contract' },
                { value: 'showing', label: 'Showing' },
                { value: 'under_contract', label: 'Under Contract' },
                { value: 'closing', label: 'Closing' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </FormField>
          <FormField label="Your Role">
            <Select
              value={form.agent_role}
              disabled={isLocked}
              onChange={(v) => update('agent_role', v)}
              options={[
                { value: 'buyer_agent', label: "Buyer's Agent" },
                { value: 'seller_agent', label: "Seller's Agent" },
                { value: 'dual_agent', label: 'Dual Agent' },
                { value: 'listing_agent', label: 'Listing Agent' },
              ]}
            />
          </FormField>
        </FormGrid>
      </SectionCard>

      {/* -------- Client Information -------- */}
      <SectionCard title="Client Information">
        <FormGrid>
          <FormField label="Client Name">
            <Input
              value={form.client_name}
              disabled={isLocked}
              onChange={(v) => update('client_name', v)}
              placeholder="Enter client name"
            />
          </FormField>
          <FormField label="Client Email">
            <Input
              type="email"
              value={form.client_email}
              disabled={isLocked}
              onChange={(v) => update('client_email', v)}
              placeholder="Enter client email"
            />
          </FormField>
        </FormGrid>
      </SectionCard>

      {/* -------- Property Information -------- */}
      <SectionCard title="Property Information">
        <FormField label="Property Address">
          <Input
            value={form.property_address}
            disabled={isLocked}
            onChange={(v) => update('property_address', v)}
            placeholder="Street address"
          />
        </FormField>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
          <FormField label="City">
            <Input
              value={form.property_city}
              disabled={isLocked}
              onChange={(v) => update('property_city', v)}
              placeholder="City"
            />
          </FormField>
          <FormField label="State">
            <Input
              value={form.property_state}
              disabled={isLocked}
              onChange={(v) => update('property_state', v.toUpperCase().slice(0, 2))}
              placeholder="ST"
            />
          </FormField>
          <FormField label="ZIP Code">
            <Input
              value={form.property_zip}
              disabled={isLocked}
              onChange={(v) => update('property_zip', v)}
              placeholder="ZIP"
            />
          </FormField>
        </div>
        <FormGrid style={{ marginTop: '12px' }}>
          <FormField label="Sale/List Price">
            <Input
              prefix="$"
              value={form.property_price}
              disabled={isLocked}
              onChange={(v) => update('property_price', v)}
              placeholder="0.00"
            />
          </FormField>
          <FormField label="Estimated Closing Date">
            <Input
              type="date"
              value={form.estimated_closing_date}
              disabled={isLocked}
              onChange={(v) => update('estimated_closing_date', v)}
            />
          </FormField>
        </FormGrid>
      </SectionCard>

      {/* -------- Property Details (drives document checklist) -------- */}
      <SectionCard title="Property Details (for Document Checklist)">
        <FormGrid>
          <FormField label="Property Type">
            <Select
              value={form.property_type}
              disabled={isLocked}
              onChange={(v) => update('property_type', v)}
              options={[
                { value: 'residential', label: 'Residential' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'land', label: 'Land' },
                { value: 'mixed_use', label: 'Mixed Use' },
              ]}
            />
          </FormField>
          <FormField label="Year Built">
            <Input
              type="number"
              value={form.year_built}
              disabled={isLocked}
              onChange={(v) => update('year_built', v)}
              placeholder="e.g. 2000"
            />
          </FormField>
          <FormField label="Loan Type">
            <Select
              value={form.loan_type}
              disabled={isLocked}
              onChange={(v) => update('loan_type', v)}
              options={[
                { value: 'conventional', label: 'Conventional' },
                { value: 'fha', label: 'FHA' },
                { value: 'va', label: 'VA' },
                { value: 'usda', label: 'USDA' },
                { value: 'cash', label: 'Cash' },
                { value: 'jumbo', label: 'Jumbo' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </FormField>
        </FormGrid>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', marginTop: '14px' }}>
          <Checkbox label="Has HOA?" checked={form.has_hoa} disabled={isLocked} onChange={(v) => update('has_hoa', v)} />
          <Checkbox label="Has Pool?" checked={form.has_pool} disabled={isLocked} onChange={(v) => update('has_pool', v)} />
          <Checkbox label="Has Solar?" checked={form.has_solar} disabled={isLocked} onChange={(v) => update('has_solar', v)} />
          <Checkbox label="Septic System?" checked={form.septic_system} disabled={isLocked} onChange={(v) => update('septic_system', v)} />
        </div>

        {form.has_solar && (
          <div style={{ marginTop: '12px', maxWidth: '300px' }}>
            <FormField label="Solar Ownership">
              <Select
                value={form.solar_ownership}
                disabled={isLocked}
                onChange={(v) => update('solar_ownership', v)}
                options={[
                  { value: 'owned', label: 'Owned' },
                  { value: 'leased', label: 'Leased' },
                  { value: 'financed', label: 'Financed' },
                ]}
              />
            </FormField>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '14px' }}>
          <FormField label="Water Source">
            <Select
              value={form.water_source}
              disabled={isLocked}
              onChange={(v) => update('water_source', v)}
              options={[
                { value: 'city', label: 'City Water' },
                { value: 'well', label: 'Private Well' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </FormField>
          <FormField label="Flood Zone">
            <Input
              value={form.flood_zone}
              disabled={isLocked}
              onChange={(v) => update('flood_zone', v)}
              placeholder="e.g. X, A, AE"
            />
          </FormField>
          <FormField label="Flood Insurance?">
            <Checkbox
              label="Required"
              checked={form.requires_flood_insurance}
              disabled={isLocked}
              onChange={(v) => update('requires_flood_insurance', v)}
            />
          </FormField>
        </div>
      </SectionCard>

      {/* -------- Notes -------- */}
      <SectionCard title="Notes">
        <textarea
          value={form.notes}
          disabled={isLocked}
          onChange={(e) => update('notes', e.target.value)}
          rows={3}
          placeholder="Additional notes..."
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: fonts.body,
            color: colors.navy,
            background: isLocked ? `${colors.muted}11` : colors.white,
            resize: 'vertical',
          }}
        />
      </SectionCard>

      {/* -------- Save / Cancel bar -------- */}
      {!isLocked && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: colors.off,
            borderTop: `1px solid ${colors.border}`,
            padding: '12px 0',
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          {saveError && (
            <span style={{ color: colors.red, fontSize: '12px', marginRight: 'auto' }}>
              ❌ {saveError}
            </span>
          )}
          {dirty && !saveError && (
            <span style={{ color: colors.orange, fontSize: '12px', marginRight: 'auto' }}>
              Unsaved changes
            </span>
          )}
          <OutlineButton onClick={() => setForm(initialForm)} disabled={!dirty || saving}>
            Revert
          </OutlineButton>
          <GoldButton onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </GoldButton>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form primitives used by OverviewPane
// ---------------------------------------------------------------------------

const SummaryRow: React.FC<{ label: string; value: string; highlight?: string }> = ({
  label,
  value,
  highlight,
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '12px' }}>
    <span style={{ color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </span>
    <strong
      style={{
        color: highlight || colors.navy,
        textTransform: 'capitalize',
      }}
    >
      {value}
    </strong>
  </div>
);

const FormGrid: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px',
      ...style,
    }}
  >
    {children}
  </div>
);

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label
      style={{
        display: 'block',
        fontSize: '11px',
        fontWeight: 700,
        color: colors.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '4px',
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const Input: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  prefix?: string;
}> = ({ value, onChange, placeholder, type = 'text', disabled, prefix }) => {
  const inputEl = (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        flex: 1,
        width: '100%',
        padding: '8px 10px',
        border: `1px solid ${colors.border}`,
        borderRadius: prefix ? '0 6px 6px 0' : '6px',
        fontSize: '13px',
        fontFamily: fonts.body,
        color: colors.navy,
        background: disabled ? `${colors.muted}11` : colors.white,
        outline: 'none',
      }}
      onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
      onBlur={(e) => (e.currentTarget.style.borderColor = colors.border)}
    />
  );
  if (prefix) {
    return (
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <span
          style={{
            background: `${colors.muted}11`,
            border: `1px solid ${colors.border}`,
            borderRight: 'none',
            borderRadius: '6px 0 0 6px',
            padding: '8px 12px',
            fontSize: '13px',
            color: colors.muted,
          }}
        >
          {prefix}
        </span>
        {inputEl}
      </div>
    );
  }
  return inputEl;
};

const Select: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
}> = ({ value, onChange, options, disabled }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    style={{
      width: '100%',
      padding: '8px 10px',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      fontSize: '13px',
      fontFamily: fonts.body,
      color: colors.navy,
      background: disabled ? `${colors.muted}11` : colors.white,
      cursor: disabled ? 'not-allowed' : 'pointer',
      outline: 'none',
    }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

const Checkbox: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '12px',
      color: colors.navy,
      cursor: disabled ? 'not-allowed' : 'pointer',
      padding: '8px 0',
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ accentColor: colors.gold, width: '16px', height: '16px' }}
    />
    {label}
  </label>
);


// ===========================================================================
// Documents pane — unified view port of DocumentChecklistWithUploads +
// BrokerReviewStatus. Renders:
//   1. Current stage banner + "Documents shown: X → Y → Z"
//   2. Document Progress counter
//   3. Stage-bucketed checklist (with upload / exempt per row + existing
//      uploaded-doc match + broker review status per row)
//   4. Additional Documents section (free-form uploads not in checklist)
//   5. Broker Review Status panel (all reviewed docs chronologically)
// ===========================================================================

interface DocumentsPaneProps {
  transaction: TransactionDetail;
  requiredDocs: RequiredDocument[];
  brokerReviews: BrokerReview[];
  uploadingFor: string | null;
  onUpload: (
    file: File,
    opts: { documentRole?: string; documentName?: string; requiredDocId?: string }
  ) => void;
  onReplace: (documentId: string, file: File) => void;
  onRefresh: () => void;
}

// Map current transaction stage → which document-stage buckets should be
// visible. "closing" means everything through post_closing is relevant.
const DOC_STAGE_VISIBILITY: Record<string, string[]> = {
  pre_contract: ['pre_contract'],
  showing: ['pre_contract'],
  under_contract: ['pre_contract', 'under_contract'],
  closing: ['pre_contract', 'under_contract', 'post_closing'],
  post_closing: ['pre_contract', 'under_contract', 'post_closing'],
  completed: ['pre_contract', 'under_contract', 'post_closing'],
  closed: ['pre_contract', 'under_contract', 'post_closing'],
};

const DOC_STAGE_LABELS: Record<string, string> = {
  pre_contract: 'Pre-Contract',
  under_contract: 'Under Contract',
  closing: 'Closing',
  post_closing: 'Post-Closing',
  completed: 'Completed',
};

// Enhance a required doc with transaction_stages extracted from ai_analysis.
// Matches the legacy DocumentChecklistWithUploads logic line-for-line:
//   - If transaction_stages is already set, use it
//   - Else parse ai_analysis.usage_context.when_required.transaction_stages
//   - If always_required is true with no stages, show in every stage
function enhanceDocWithStages(doc: RequiredDocument): RequiredDocument {
  if (doc.transaction_stages?.length) return doc;
  if (!doc.ai_analysis) return doc;
  try {
    const analysis =
      typeof doc.ai_analysis === 'string' ? JSON.parse(doc.ai_analysis) : doc.ai_analysis;
    const stages: string[] | undefined = analysis?.usage_context?.when_required?.transaction_stages;
    if (stages && stages.length > 0) {
      return {
        ...doc,
        transaction_stages: stages.map((s) => s.toLowerCase().replace(/[\s-]/g, '_')),
      };
    }
    if (analysis?.usage_context?.when_required?.always_required) {
      return {
        ...doc,
        transaction_stages: ['pre_contract', 'showing', 'under_contract', 'closing'],
      };
    }
  } catch { /* ignore parse errors */ }
  return doc;
}

// Bucket documents by their transaction_stages with timing-based fallbacks.
function bucketDocsByStage(docs: RequiredDocument[]): Record<string, RequiredDocument[]> {
  const buckets: Record<string, RequiredDocument[]> = {
    pre_contract: [],
    under_contract: [],
    post_closing: [],
  };
  docs.forEach((doc) => {
    const stages = doc.transaction_stages || [];
    if (stages.includes('pre_contract')) buckets.pre_contract.push(doc);
    else if (stages.includes('under_contract')) buckets.under_contract.push(doc);
    else if (stages.includes('closing') || stages.includes('post_closing'))
      buckets.post_closing.push(doc);
    else {
      const timing = (doc.timing || '').toLowerCase();
      if (
        timing.includes('immediately') ||
        timing.includes('when making offer') ||
        timing.includes('before offer') ||
        !timing
      ) {
        buckets.pre_contract.push(doc);
      } else if (
        (timing.includes('within') && timing.includes('days')) ||
        timing.includes('inspection') ||
        timing.includes('contingency') ||
        timing.includes('after acceptance')
      ) {
        buckets.under_contract.push(doc);
      } else if (
        timing.includes('closing') ||
        timing.includes('settlement') ||
        timing.includes('before closing')
      ) {
        buckets.post_closing.push(doc);
      } else {
        buckets.pre_contract.push(doc);
      }
    }
  });
  return buckets;
}

// Match a required checklist doc to an uploaded doc from the broker-review
// response. Legacy matches on: document_id, then name, then filename, then
// description (all case-insensitive). Wire Fraud gets a fuzzy match because
// its template name and uploaded filename differ a lot.
function matchUploadedDoc(
  required: RequiredDocument,
  uploaded: BrokerReview[]
): BrokerReview | undefined {
  const checklistName = (required.name || '').toLowerCase();
  return uploaded.find((existing) => {
    if (existing.document_id === required.id || existing.id === required.id) return true;
    const uploadedName = (existing.name || existing.filename || existing.original_filename || '').toLowerCase();
    const uploadedDesc = (existing.description || '').toLowerCase();
    if (uploadedName === checklistName || uploadedDesc === checklistName) return true;
    if (
      checklistName.includes('wire fraud') &&
      (uploadedName.includes('wire fraud') || uploadedDesc.includes('wire fraud'))
    ) {
      return true;
    }
    return false;
  });
}

function DocumentsPane({
  transaction,
  requiredDocs,
  brokerReviews,
  uploadingFor,
  onUpload,
  onReplace,
  onRefresh,
}: DocumentsPaneProps) {
  const enhancedRequired = useMemo(
    () => requiredDocs.map(enhanceDocWithStages),
    [requiredDocs]
  );
  const buckets = useMemo(() => bucketDocsByStage(enhancedRequired), [enhancedRequired]);

  const currentStage = (transaction.stage || 'pre_contract').toLowerCase();
  const visibleStageKeys = DOC_STAGE_VISIBILITY[currentStage] || ['pre_contract'];

  const totalRequired = enhancedRequired.length;
  const uploadedCount = enhancedRequired.filter((r) => !!matchUploadedDoc(r, brokerReviews)).length;

  const checklistIds = new Set(enhancedRequired.map((r) => r.id));
  const checklistNames = new Set(enhancedRequired.map((r) => (r.name || '').toLowerCase()));
  const additionalDocs = brokerReviews.filter((d) => {
    if (d.document_id && checklistIds.has(d.document_id)) return false;
    if (d.id && checklistIds.has(d.id)) return false;
    const n = (d.name || d.filename || d.original_filename || '').toLowerCase();
    if (n && checklistNames.has(n)) return false;
    return true;
  });

  const reviewedDocs = brokerReviews.filter((d) => d.broker_review_status != null);
  const pendingCount = reviewedDocs.filter((d) => d.broker_review_status === 'pending_review').length;
  const approvedCount = reviewedDocs.filter((d) => d.broker_review_status === 'approved').length;
  const revisionCount = reviewedDocs.filter(
    (d) =>
      d.broker_review_status === 'needs_revision' ||
      d.broker_review_status === 'changes_requested' ||
      d.broker_review_status === 'rejected'
  ).length;

  return (
    <div style={{ paddingTop: '16px' }}>
      {/* Current Stage Indicator */}
      <div
        style={{
          background: `${colors.blue}0a`,
          border: `1px solid ${colors.blue}44`,
          borderLeft: `3px solid ${colors.blue}`,
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '12px',
          fontSize: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <FontAwesomeIcon icon={faClipboardCheck} style={{ color: colors.blue }} />
          <strong style={{ color: colors.navy }}>Transaction Stage:</strong>
          <StageBadge stage={transaction.stage} />
        </div>
        <div style={{ color: colors.muted, fontSize: '11px' }}>
          Documents shown:{' '}
          {visibleStageKeys.map((s, i) => (
            <span key={s}>
              {i > 0 && ' → '}
              <strong style={{ color: colors.navy }}>{DOC_STAGE_LABELS[s]}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Document Progress Counter */}
      <div
        style={{
          background: `${colors.gold}0a`,
          border: `1px solid ${colors.gold}44`,
          borderLeft: `3px solid ${colors.gold}`,
          borderRadius: '6px',
          padding: '10px 16px',
          marginBottom: '16px',
          fontSize: '13px',
          color: colors.navy,
        }}
      >
        <strong>Compliance:</strong>{' '}
        {totalRequired > 0 ? Math.round((uploadedCount / totalRequired) * 100) : 0}% for this stage
        {' — '}
        {Math.max(0, totalRequired - uploadedCount)} of {totalRequired} required document
        {totalRequired - uploadedCount === 1 ? '' : 's'} remaining
      </div>

      {/* Stage-bucketed checklist */}
      {visibleStageKeys.map((stageKey) => {
        const stageDocs = buckets[stageKey] || [];
        if (stageDocs.length === 0) return null;
        const stageUploaded = stageDocs.filter((d) => !!matchUploadedDoc(d, brokerReviews)).length;
        return (
          <SectionCard
            key={stageKey}
            title={`📋 ${DOC_STAGE_LABELS[stageKey]} Documents`}
            subtitle={`${stageUploaded}/${stageDocs.length} Complete`}
          >
            {stageDocs.map((doc) => {
              const uploaded = matchUploadedDoc(doc, brokerReviews);
              return (
                <ChecklistRowItem
                  key={doc.id}
                  required={doc}
                  uploaded={uploaded}
                  uploading={uploadingFor === doc.id || uploadingFor === doc.name}
                  onUpload={(file) =>
                    onUpload(file, {
                      documentRole: doc.type || doc.document_type || doc.name,
                      documentName: doc.name,
                      requiredDocId: doc.id,
                    })
                  }
                  onExempt={async (reason) => {
                    try {
                      if (reason === null) {
                        await axiosInstance.delete(
                          `/transaction-exemptions/transactions/${transaction.id}/documents/${doc.id}/exempt`
                        );
                      } else {
                        await axiosInstance.post(
                          `/transaction-exemptions/transactions/${transaction.id}/documents/${doc.id}/exempt`,
                          { reason, exempted_by_role: 'agent' }
                        );
                      }
                      onRefresh();
                    } catch (err) {
                      console.error('Exemption failed:', err);
                    }
                  }}
                  onReplace={(file) => {
                    if (uploaded?.id) onReplace(uploaded.id, file);
                  }}
                />
              );
            })}
          </SectionCard>
        );
      })}

      {/* Additional Documents */}
      <SectionCard
        title={`📄 Additional Documents${additionalDocs.length > 0 ? ` (${additionalDocs.length})` : ''}`}
        subtitle="Uploaded files not tied to a required checklist slot"
      >
        {additionalDocs.length === 0 ? (
          <div style={{ padding: '12px 0', color: colors.muted, fontSize: '12px' }}>
            No additional documents uploaded.
          </div>
        ) : (
          additionalDocs.map((doc) => (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 0',
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <FontAwesomeIcon icon={faFileContract} style={{ color: colors.gold2, fontSize: '14px' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: colors.navy,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {doc.original_filename || doc.filename || doc.name || 'Document'}
                </div>
                {doc.broker_review_status && (
                  <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
                    Status: {doc.broker_review_status}
                    {doc.broker_compliance_score != null && ` · Score: ${doc.broker_compliance_score}%`}
                  </div>
                )}
              </div>
              {doc.s3_url && (
                <a
                  href={doc.s3_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.gold,
                    fontSize: '12px',
                    fontWeight: 600,
                    textDecoration: 'none',
                    padding: '4px 10px',
                  }}
                >
                  <FontAwesomeIcon icon={faEye} /> View
                </a>
              )}
            </div>
          ))
        )}
      </SectionCard>

      {/* Broker Review Status panel */}
      {reviewedDocs.length > 0 && (
        <SectionCard
          title="Broker Review Status"
          subtitle={`${pendingCount} Pending · ${approvedCount} Approved${revisionCount > 0 ? ` · ${revisionCount} Need Revision` : ''}`}
        >
          {reviewedDocs
            .slice()
            .sort((a, b) => {
              const aDate = new Date(a.broker_reviewed_at || a.added_at || a.created_at || 0).getTime();
              const bDate = new Date(b.broker_reviewed_at || b.added_at || b.created_at || 0).getTime();
              return bDate - aDate;
            })
            .map((doc) => {
              const status = doc.broker_review_status || 'pending';
              const isApproved = status === 'approved';
              const needsRevision =
                status === 'needs_revision' || status === 'changes_requested' || status === 'rejected';
              const badgeColor = isApproved ? colors.green : needsRevision ? colors.red : colors.orange;
              return (
                <div
                  key={doc.id}
                  style={{
                    padding: '12px 0',
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '3px' }}>
                        {doc.original_filename || doc.filename || doc.name || 'Document'}
                      </div>
                      {doc.description &&
                        doc.description !== doc.original_filename &&
                        doc.description !== doc.filename && (
                          <div style={{ fontSize: '11px', color: colors.muted, marginBottom: '3px' }}>
                            {doc.description}
                          </div>
                        )}
                      <div style={{ fontSize: '11px', color: colors.muted }}>
                        Submitted: {doc.added_at ? new Date(doc.added_at).toLocaleString() : '—'}
                      </div>
                      {doc.broker_reviewed_at && (
                        <div style={{ fontSize: '11px', color: colors.muted }}>
                          Reviewed: {new Date(doc.broker_reviewed_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          background: `${badgeColor}22`,
                          color: badgeColor,
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {isApproved ? '✅ Approved' : needsRevision ? '⚠️ Revision' : '⏳ Pending'}
                      </span>
                      {doc.broker_compliance_score != null && (
                        <div style={{ fontSize: '11px', color: colors.muted, marginTop: '4px' }}>
                          Score: {doc.broker_compliance_score}%
                        </div>
                      )}
                    </div>
                  </div>
                  {doc.broker_review_notes && (
                    <div
                      style={{
                        marginTop: '8px',
                        background: needsRevision ? `${colors.red}0a` : `${colors.blue}0a`,
                        border: `1px solid ${needsRevision ? colors.red : colors.blue}44`,
                        borderLeft: `3px solid ${needsRevision ? colors.red : colors.blue}`,
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '11px',
                        color: '#555',
                        lineHeight: 1.5,
                      }}
                    >
                      <strong style={{ color: needsRevision ? colors.red : colors.navy }}>
                        {needsRevision ? 'Revision Required: ' : 'Broker Notes: '}
                      </strong>
                      {doc.broker_review_notes}
                    </div>
                  )}
                </div>
              );
            })}
        </SectionCard>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChecklistRowItem — one required-doc row with upload / exempt / replace
// ---------------------------------------------------------------------------

function ChecklistRowItem({
  required,
  uploaded,
  uploading,
  onUpload,
  onExempt,
  onReplace,
}: {
  required: RequiredDocument;
  uploaded?: BrokerReview;
  uploading: boolean;
  onUpload: (file: File) => void;
  onExempt: (reason: string | null) => void;
  onReplace: (file: File) => void;
}) {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const [showExemptForm, setShowExemptForm] = useState(false);
  const [exemptReason, setExemptReason] = useState('');

  const isExempt = uploaded?.status === 'exempt' || !!uploaded?.exemption_id;
  const hasUpload = !!uploaded && !isExempt;
  const status = uploaded?.broker_review_status;
  const isApproved = status === 'approved';
  const needsRevision =
    status === 'needs_revision' || status === 'changes_requested' || status === 'rejected';

  const rowBg = isExempt
    ? `${colors.muted}0a`
    : isApproved
    ? `${colors.green}0a`
    : needsRevision
    ? `${colors.red}0a`
    : hasUpload
    ? `${colors.blue}0a`
    : colors.white;

  return (
    <div
      style={{
        background: rowBg,
        border: `1px solid ${colors.border}`,
        borderRadius: '6px',
        padding: '12px 14px',
        marginBottom: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div
          style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: isApproved
              ? `${colors.green}22`
              : isExempt
              ? `${colors.muted}22`
              : hasUpload
              ? `${colors.blue}22`
              : `${colors.muted}11`,
            border: `2px solid ${
              isApproved ? colors.green : isExempt ? colors.muted : hasUpload ? colors.blue : colors.border
            }`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px',
          }}
        >
          <FontAwesomeIcon
            icon={isApproved ? faCheckCircle : isExempt ? faCircleInfo : hasUpload ? faCloudArrowUp : faFileContract}
            style={{
              color: isApproved ? colors.green : isExempt ? colors.muted : hasUpload ? colors.blue : colors.muted,
              fontSize: '13px',
            }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy, marginBottom: '2px' }}>
            {required.name}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span
              style={{
                background: `${colors.muted}22`,
                color: colors.navy,
                fontSize: '9px',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: '3px',
                textTransform: 'uppercase',
              }}
            >
              Required
            </span>
            {isExempt && (
              <span
                style={{
                  background: `${colors.muted}22`,
                  color: colors.muted,
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                }}
              >
                Exempt
              </span>
            )}
            {isApproved && (
              <span
                style={{
                  background: `${colors.green}22`,
                  color: colors.green,
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                }}
              >
                ✓ Broker Approved
              </span>
            )}
            {needsRevision && (
              <span
                style={{
                  background: `${colors.red}22`,
                  color: colors.red,
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                }}
              >
                ⚠️ Needs Revision
              </span>
            )}
            {hasUpload && !isApproved && !needsRevision && (
              <span
                style={{
                  background: `${colors.orange}22`,
                  color: colors.orange,
                  fontSize: '9px',
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: '3px',
                  textTransform: 'uppercase',
                }}
              >
                ⏳ Pending Review
              </span>
            )}
            {uploaded?.broker_compliance_score != null && (
              <span style={{ fontSize: '10px', color: colors.muted }}>
                Score: {uploaded.broker_compliance_score}%
              </span>
            )}
          </div>
          {hasUpload && (
            <div style={{ fontSize: '11px', color: colors.muted }}>
              {uploaded.original_filename || uploaded.filename}
              {uploaded.broker_reviewed_at && (
                <> · Reviewed on {new Date(uploaded.broker_reviewed_at).toLocaleDateString()}</>
              )}
            </div>
          )}
          {isExempt && uploaded?.exemption_reason && (
            <div style={{ fontSize: '11px', color: colors.muted, fontStyle: 'italic' }}>
              Exemption reason: {uploaded.exemption_reason}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {!hasUpload && !isExempt && (
            <>
              <input
                ref={uploadInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(file);
                  if (e.target) e.target.value = '';
                }}
              />
              <OutlineButton onClick={() => uploadInputRef.current?.click()} disabled={uploading}>
                <FontAwesomeIcon icon={faCloudArrowUp} /> {uploading ? 'Uploading…' : 'Upload'}
              </OutlineButton>
              <OutlineButton
                onClick={() => setShowExemptForm(!showExemptForm)}
                style={{ borderColor: colors.muted, color: colors.muted }}
              >
                Exempt
              </OutlineButton>
            </>
          )}
          {hasUpload && needsRevision && (
            <>
              <input
                ref={replaceInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onReplace(file);
                  if (e.target) e.target.value = '';
                }}
              />
              <OutlineButton
                onClick={() => replaceInputRef.current?.click()}
                disabled={uploading}
                style={{ borderColor: colors.orange, color: colors.orange }}
              >
                <FontAwesomeIcon icon={faRotate} /> Resubmit
              </OutlineButton>
            </>
          )}
          {hasUpload && uploaded?.s3_url && (
            <a
              href={uploaded.s3_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.gold,
                fontSize: '12px',
                fontWeight: 600,
                textDecoration: 'none',
                padding: '6px 10px',
                border: `1px solid ${colors.gold}`,
                borderRadius: '4px',
              }}
            >
              <FontAwesomeIcon icon={faEye} /> View
            </a>
          )}
          {isExempt && (
            <OutlineButton
              onClick={() => onExempt(null)}
              style={{ borderColor: colors.muted, color: colors.muted }}
            >
              Un-exempt
            </OutlineButton>
          )}
        </div>
      </div>

      {showExemptForm && !hasUpload && !isExempt && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            background: `${colors.muted}0a`,
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}
          >
            Exemption Reason
          </label>
          <input
            type="text"
            value={exemptReason}
            onChange={(e) => setExemptReason(e.target.value)}
            placeholder="e.g., Not applicable, waived by broker…"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              fontSize: '13px',
              marginBottom: '8px',
            }}
          />
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <OutlineButton
              onClick={() => {
                setShowExemptForm(false);
                setExemptReason('');
              }}
            >
              Cancel
            </OutlineButton>
            <GoldButton
              onClick={() => {
                if (exemptReason.trim()) {
                  onExempt(exemptReason.trim());
                  setShowExemptForm(false);
                  setExemptReason('');
                }
              }}
              disabled={!exemptReason.trim()}
            >
              Confirm Exemption
            </GoldButton>
          </div>
        </div>
      )}

      {uploaded?.broker_review_notes && (
        <div
          style={{
            marginTop: '10px',
            marginLeft: '42px',
            background: needsRevision ? `${colors.red}0a` : `${colors.blue}0a`,
            border: `1px solid ${needsRevision ? colors.red : colors.blue}44`,
            borderLeft: `3px solid ${needsRevision ? colors.red : colors.blue}`,
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '11px',
            color: '#555',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: needsRevision ? colors.red : colors.navy }}>
            {needsRevision ? 'Revision Required: ' : 'Broker Notes: '}
          </strong>
          {uploaded.broker_review_notes}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parties / Timeline panes
// ---------------------------------------------------------------------------

const PartiesPane: React.FC<{ parties: NonNullable<TransactionDetail['parties']> }> = ({ parties }) => (
  <SectionCard title={`Parties (${parties.length})`}>
    {parties.length === 0 ? (
      <div style={{ padding: '20px 0', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
        No parties linked to this transaction yet
      </div>
    ) : (
      parties.map((p) => (
        <div
          key={p.id}
          style={{
            padding: '10px 0',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy }}>
              {p.full_name || '(unnamed)'}
              {p.is_primary && (
                <span
                  style={{
                    marginLeft: '8px',
                    fontSize: '9px',
                    background: `${colors.gold}22`,
                    color: colors.gold,
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  Primary
                </span>
              )}
            </div>
            {p.email && <div style={{ fontSize: '12px', color: colors.muted }}>{p.email}</div>}
            {p.phone && <div style={{ fontSize: '12px', color: colors.muted }}>{p.phone}</div>}
          </div>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              background: `${colors.blue}22`,
              color: colors.blue,
              padding: '3px 8px',
              borderRadius: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {p.role || 'party'}
          </span>
        </div>
      ))
    )}
  </SectionCard>
);

const TimelinePane: React.FC<{ transaction: TransactionDetail; brokerReviews: BrokerReview[] }> = ({
  transaction,
  brokerReviews,
}) => {
  // Merge transaction events + review events into a single sorted timeline
  const events = useMemo(() => {
    const list: Array<{ when: string; label: string; icon: any; color: string }> = [];
    if (transaction.created_at) {
      list.push({
        when: transaction.created_at,
        label: 'Transaction created',
        icon: faFileContract,
        color: colors.blue,
      });
    }
    if (transaction.updated_at && transaction.updated_at !== transaction.created_at) {
      list.push({
        when: transaction.updated_at,
        label: `Stage: ${STAGE_LABELS[transaction.stage || ''] || transaction.stage || '—'}`,
        icon: faClockRotateLeft,
        color: colors.gold2,
      });
    }
    brokerReviews.forEach((r) => {
      if (!r.reviewed_at) return;
      const needsRevision = r.review_status === 'needs_revision' || r.review_status === 'rejected';
      list.push({
        when: r.reviewed_at,
        label: `Broker ${r.review_status?.replace('_', ' ') || 'reviewed'}: ${r.document_name || 'document'}`,
        icon: needsRevision ? faTriangleExclamation : faCheckCircle,
        color: needsRevision ? colors.orange : colors.green,
      });
    });
    if (transaction.closed_at) {
      list.push({
        when: transaction.closed_at,
        label: 'Transaction closed',
        icon: faCheckCircle,
        color: colors.green,
      });
    }
    return list.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
  }, [transaction, brokerReviews]);

  return (
    <SectionCard title="Timeline">
      {events.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
          No events yet
        </div>
      ) : (
        events.map((e, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: '12px',
              padding: '10px 0',
              borderBottom: `1px solid ${colors.border}`,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: `${e.color}22`,
                border: `2px solid ${e.color}55`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={e.icon} style={{ color: e.color, fontSize: '11px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 500, color: colors.navy }}>{e.label}</div>
              <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>
                {formatAbsoluteTime(e.when)}
              </div>
            </div>
          </div>
        ))
      )}
    </SectionCard>
  );
};

// ---------------------------------------------------------------------------
// Closing pane — broker-only, visible when stage is under_contract/closing/closed
// ---------------------------------------------------------------------------

const ClosingPane: React.FC<{
  transaction: TransactionDetail;
  onRefresh: () => void;
  onAlert: (msg: string) => void;
}> = ({ transaction, onRefresh, onAlert }) => {
  const [advancing, setAdvancing] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [confirmAdvance, setConfirmAdvance] = useState(false);
  const [confirmDistribute, setConfirmDistribute] = useState(false);

  const currentStage = transaction.stage || '';
  const isClosed = currentStage === 'closed';
  const nextStage =
    currentStage === 'under_contract' ? 'closing' :
    currentStage === 'closing' ? 'closed' :
    null;
  const nextStageLabel =
    nextStage === 'closing' ? 'Closing' :
    nextStage === 'closed' ? 'Closed' :
    null;

  // Quick checklist heuristics — what's visibly complete on this transaction
  const documents = transaction.documents || [];
  const parties = transaction.parties || [];
  const docsApproved = documents.filter(
    (d) => d.review_status === 'approved'
  ).length;
  const docsTotal = documents.length;
  const hasPrimaryParty = parties.some((p) => p.is_primary);
  const hasPropertyData = !!(transaction.property?.address || transaction.property?.price);

  const handleAdvanceStage = async () => {
    if (!nextStage) return;
    setAdvancing(true);
    try {
      await axiosInstance.put(`/transactions/${transaction.id}/stage`, {
        stage: nextStage,
      });
      onAlert(`✅ Transaction advanced to ${nextStageLabel}`);
      setConfirmAdvance(false);
      onRefresh();
    } catch (err: any) {
      onAlert(`❌ ${err?.response?.data?.error || err?.message || 'Stage advance failed'}`);
    } finally {
      setAdvancing(false);
    }
  };

  const handleDistributeCommission = async () => {
    setDistributing(true);
    try {
      await axiosInstance.post(`/transaction-closing/commission-distributions`, {
        transaction_id: transaction.id,
      });
      onAlert('✅ Commission distribution recorded');
      setConfirmDistribute(false);
      onRefresh();
    } catch (err: any) {
      onAlert(`❌ ${err?.response?.data?.error || err?.message || 'Distribution failed'}`);
    } finally {
      setDistributing(false);
    }
  };

  return (
    <>
      <div
        style={{
          background: `${colors.gold}11`,
          border: `1px solid ${colors.gold}44`,
          borderLeft: `3px solid ${colors.gold}`,
          borderRadius: '6px',
          padding: '12px 14px',
          fontSize: '12px',
          color: '#555',
          lineHeight: 1.5,
          marginBottom: '12px',
        }}
      >
        <FontAwesomeIcon icon={faLock} style={{ color: colors.gold, marginRight: '6px' }} />
        <strong>Broker Closing Workspace.</strong> Only brokerage admins see this tab. Use it to
        advance the transaction through closing stages and distribute commissions once the deal
        funds.
      </div>

      <SectionCard title="Closing Readiness">
        <ReadinessRow
          label="Documents approved"
          done={docsTotal > 0 && docsApproved === docsTotal}
          detail={`${docsApproved} of ${docsTotal} documents approved by broker`}
        />
        <ReadinessRow
          label="Primary party assigned"
          done={hasPrimaryParty}
          detail={hasPrimaryParty ? 'Buyer/seller on record' : 'Add primary party to proceed'}
        />
        <ReadinessRow
          label="Property data"
          done={hasPropertyData}
          detail={hasPropertyData ? transaction.property?.address || 'Address on file' : 'Missing property details'}
        />
        <ReadinessRow
          label="Compliance status"
          done={transaction.compliance_status === 'compliant' || transaction.compliance_status === 'approved'}
          detail={`Current: ${transaction.compliance_status || 'unknown'}`}
        />
      </SectionCard>

      <SectionCard title="Stage Advancement">
        <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '12px' }}>
          Current stage: <strong style={{ color: colors.navy }}>{STAGE_LABELS[currentStage] || currentStage}</strong>
        </div>
        {isClosed ? (
          <div
            style={{
              padding: '14px',
              background: `${colors.green}11`,
              border: `1px solid ${colors.green}44`,
              borderRadius: '6px',
              textAlign: 'center',
              color: colors.green,
              fontWeight: 600,
              fontSize: '13px',
            }}
          >
            <FontAwesomeIcon icon={faCheckCircle} /> Transaction closed
          </div>
        ) : confirmAdvance ? (
          <div>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px', lineHeight: 1.5 }}>
              This will advance the transaction from{' '}
              <strong>{STAGE_LABELS[currentStage] || currentStage}</strong> to{' '}
              <strong>{nextStageLabel}</strong>. The agent will see the updated stage in their
              timeline. This action is visible to the broker only and cannot be undone without
              manual intervention.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <OutlineButton onClick={() => setConfirmAdvance(false)} disabled={advancing}>
                Cancel
              </OutlineButton>
              <GoldButton onClick={handleAdvanceStage} disabled={advancing}>
                <FontAwesomeIcon icon={faForward} /> {advancing ? 'Advancing…' : `Confirm → ${nextStageLabel}`}
              </GoldButton>
            </div>
          </div>
        ) : (
          <OutlineButton
            onClick={() => setConfirmAdvance(true)}
            disabled={!nextStage}
            style={{ borderColor: colors.gold, color: colors.gold }}
          >
            <FontAwesomeIcon icon={faForward} /> Advance to {nextStageLabel || 'next stage'}
          </OutlineButton>
        )}
      </SectionCard>

      <SectionCard title="Commission Distribution">
        <div style={{ fontSize: '12px', color: colors.muted, marginBottom: '12px', lineHeight: 1.5 }}>
          Record the commission distribution for this transaction. This posts to{' '}
          <code style={{ background: colors.off, padding: '1px 4px', borderRadius: '3px' }}>
            POST /transaction-closing/commission-distributions
          </code>{' '}
          and creates a distribution record the broker can reconcile with their accounting.
        </div>
        {confirmDistribute ? (
          <div>
            <div
              style={{
                padding: '10px 12px',
                background: `${colors.orange}11`,
                border: `1px solid ${colors.orange}44`,
                borderLeft: `3px solid ${colors.orange}`,
                borderRadius: '6px',
                fontSize: '12px',
                color: '#555',
                marginBottom: '10px',
              }}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: colors.orange, marginRight: '6px' }} />
              Confirm you want to record the commission distribution for this transaction.
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <OutlineButton onClick={() => setConfirmDistribute(false)} disabled={distributing}>
                Cancel
              </OutlineButton>
              <GoldButton onClick={handleDistributeCommission} disabled={distributing}>
                <FontAwesomeIcon icon={faHandHoldingDollar} /> {distributing ? 'Recording…' : 'Confirm Distribution'}
              </GoldButton>
            </div>
          </div>
        ) : (
          <OutlineButton
            onClick={() => setConfirmDistribute(true)}
            style={{ borderColor: colors.green, color: colors.green }}
          >
            <FontAwesomeIcon icon={faHandHoldingDollar} /> Distribute Commission
          </OutlineButton>
        )}
      </SectionCard>

      <div style={{ fontSize: '11px', color: colors.muted, textAlign: 'center', padding: '8px 0' }}>
        Full closing manager (commission overrides, 1099 payments, expense tracking) lives in the
        legacy broker portal and will migrate in a follow-up pass.
      </div>
    </>
  );
};

const ReadinessRow: React.FC<{ label: string; done: boolean; detail?: string }> = ({
  label,
  done,
  detail,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '8px 0',
      borderBottom: `1px solid ${colors.border}`,
    }}
  >
    <FontAwesomeIcon
      icon={done ? faCheckCircle : faCircleExclamation}
      style={{ color: done ? colors.green : colors.orange, fontSize: '14px' }}
    />
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: colors.navy }}>{label}</div>
      {detail && (
        <div style={{ fontSize: '11px', color: colors.muted, marginTop: '2px' }}>{detail}</div>
      )}
    </div>
  </div>
);

// ===========================================================================
// Small helpers
// ===========================================================================

const SectionCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          fontSize: '11px',
          fontWeight: 700,
          color: colors.navy,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: '11px', color: colors.muted, marginTop: '3px', fontStyle: 'italic' }}>
          {subtitle}
        </div>
      )}
    </div>
    {children}
  </div>
);

const StageBadge: React.FC<{ stage?: string }> = ({ stage }) => {
  const color = STAGE_COLORS[stage || ''] || colors.muted;
  const label = STAGE_LABELS[stage || ''] || stage || 'Unknown';
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const ComplianceBadge: React.FC<{ status?: string }> = ({ status }) => {
  const color =
    status === 'compliant' || status === 'approved'
      ? colors.green
      : status === 'violation'
      ? colors.red
      : colors.orange;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {status || 'unknown'}
    </span>
  );
};

const ComplianceIcon: React.FC<{ status?: string }> = ({ status }) => {
  if (status === 'compliant' || status === 'approved') {
    return <FontAwesomeIcon icon={faCheckCircle} style={{ color: colors.green, fontSize: '11px' }} />;
  }
  if (status === 'violation') {
    return <FontAwesomeIcon icon={faCircleExclamation} style={{ color: colors.red, fontSize: '11px' }} />;
  }
  return <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: colors.orange, fontSize: '11px' }} />;
};

const ReviewStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || 'pending').toLowerCase();
  const color =
    s === 'approved'
      ? colors.green
      : s === 'needs_revision' || s === 'rejected'
      ? colors.orange
      : colors.blue;
  const label =
    s === 'approved'
      ? 'Approved'
      : s === 'needs_revision'
      ? 'Needs Revision'
      : s === 'rejected'
      ? 'Rejected'
      : 'Pending Review';
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

function formatRelativeTime(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

function formatAbsoluteTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

// ===========================================================================
// Landing Dashboard — shown in the right panel when no transaction is
// selected. 1:1 port of EnhancedAgentDashboard in navy+gold.
//   - Welcome header:     agent name, title, rank, license, brokerage info
//   - 6 KPI cards:        active / closed / pipeline / commission / avg days
//                         / monthly goal progress bar
//   - Active Files card:  clickable list of non-closed transactions
//   - Recent Broker Reviews card: aggregated across top 10 transactions
// ===========================================================================

interface LandingDashboardProps {
  agentInfo: AgentInfo;
  brokerageInfo: BrokerageInfo;
  metrics: AgentMetrics;
  transactions: Transaction[];
  landingReviews: LandingBrokerReview[];
  onSelectTransaction: (t: Transaction) => void;
}

// Declared as `function` (not `const`) so it's hoisted — the main
// AgentDocumentsHub component above renders <LandingDashboard />, and
// TypeScript treats forward const-arrow-function references as errors.
function LandingDashboard({
  agentInfo,
  brokerageInfo,
  metrics,
  transactions,
  landingReviews,
  onSelectTransaction,
}: LandingDashboardProps) {
  // Derive active files (non-closed/non-cancelled) from the main tx list
  // so we don't have to refetch. Keeps the data source single.
  const activeFiles = useMemo(
    () =>
      transactions.filter(
        (t) => t.stage !== 'completed' && t.stage !== 'cancelled' && t.stage !== 'closed'
      ),
    [transactions]
  );

  // averageDaysToClose is explicitly null (not 0) when no deals have been
  // closed yet — render "—" in that case instead of misleading the agent
  // with a hardcoded default. Commission and monthly goal intentionally
  // omitted from the landing (see commit message for KOPS-465 reasoning).
  const stats = {
    activeTransactions: metrics.active ?? activeFiles.length,
    closedThisMonth: metrics.closedThisMonth ?? 0,
    pipelineValue: metrics.totalPipelineValue ?? 0,
    averageDaysToClose:
      metrics.averageDaysToClose == null ? '—' : String(metrics.averageDaysToClose),
  };

  const approvedCount = landingReviews.filter((r) => r.status === 'approved').length;
  const pendingCount = landingReviews.filter((r) => r.status === 'pending').length;
  const revisionCount = landingReviews.filter((r) => r.status === 'needs_revision').length;

  return (
    <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
      {/* Welcome header */}
      <div
        style={{
          background: colors.navy2,
          borderRadius: '12px',
          padding: '24px',
          border: `1px solid ${colors.navy3}`,
          borderLeft: `4px solid ${colors.gold}`,
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(15, 30, 53, 0.08)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: 0 }}>
            <div
              style={{
                width: '60px',
                height: '60px',
                background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold3} 100%)`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 16px rgba(201, 149, 42, 0.3)`,
                flexShrink: 0,
              }}
            >
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: '24px', color: colors.navy }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: fonts.display,
                  fontSize: '28px',
                  color: colors.white,
                  marginBottom: '6px',
                  letterSpacing: '1px',
                }}
              >
                {agentInfo.name ? `Welcome back, ${agentInfo.name}` : 'Welcome back'}
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span
                  style={{
                    background: colors.gold,
                    color: colors.navy,
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '3px 10px',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {agentInfo.title}
                </span>
                {/* "Top Performer" rank badge removed — was hardcoded in
                    legacy and has no data source. Add back when we have a
                    real ranking system (top X% closer, pipeline leader, etc). */}
                <span style={{ fontSize: '12px', color: colors.muted }}>
                  License: {agentInfo.licenseNumber}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: colors.off, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <FontAwesomeIcon icon={faBuilding} style={{ color: colors.gold2 }} />
                <strong style={{ color: colors.white }}>{brokerageInfo.brokerageName || 'Your Brokerage'}</strong>
                {brokerageInfo.brokerName && (
                  <>
                    <span style={{ color: colors.muted }}>•</span>
                    <span>Broker: {brokerageInfo.brokerName}</span>
                  </>
                )}
                {(brokerageInfo.city || brokerageInfo.state) && (
                  <>
                    <span style={{ color: colors.muted }}>•</span>
                    <span>{[brokerageInfo.city, brokerageInfo.state].filter(Boolean).join(', ')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Removed the "Team of X agents · Y total transactions" strip —
              agent count is broker-side metadata and not useful on the
              agent's own landing, and total transactions is already
              shown on the Active Transactions KPI card below. Avoiding
              the duplicate "0 · 0" display when those values are unknown. */}
        </div>
      </div>

      {/* KPI cards — 4 real, actionable metrics only.
          Commission Earned is intentionally NOT on the landing (sensitive
          payroll data, shoulder-surfing risk — belongs in a dedicated
          /agent-portal/earnings report with its own access log, not on
          the default page that's open during client demos).
          Monthly Goal is deferred until we have a real data source
          (needs a monthly_close_goal column on Users / agent_profiles). */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        <KpiCard
          icon={faHandHoldingDollar}
          iconColor={colors.blue}
          value={String(stats.activeTransactions)}
          label="Active Transactions"
        />
        <KpiCard
          icon={faCheckCircle}
          iconColor={colors.green}
          value={String(stats.closedThisMonth)}
          label="Closed This Month"
        />
        <KpiCard
          icon={faDollarSign}
          iconColor={colors.gold}
          value={formatCurrencyCompact(stats.pipelineValue)}
          label="Pipeline Value"
        />
        <KpiCard
          icon={faCalendarAlt}
          iconColor={colors.orange}
          value={stats.averageDaysToClose}
          label="Avg Days to Close"
        />
      </div>

      {/* Active Files + Recent Broker Reviews */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
          gap: '16px',
        }}
      >
        {/* Active Files */}
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderLeft: `4px solid ${colors.blue}`,
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
              📁 Active Files ({activeFiles.length})
            </div>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {activeFiles.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '13px' }}>
                No active transactions at the moment
              </div>
            ) : (
              activeFiles.map((file, i) => {
                const days = Math.floor(
                  (Date.now() - new Date(file.updated_at || file.created_at || Date.now()).getTime()) /
                    86400000
                );
                return (
                  <div
                    key={file.id}
                    onClick={() => onSelectTransaction(file)}
                    style={{
                      padding: '14px 16px',
                      borderBottom: i === activeFiles.length - 1 ? 'none' : `1px solid ${colors.border}`,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = `${colors.blue}0a`)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 600, color: colors.navy, marginBottom: '4px' }}>
                      {file.property?.address || `${file.client_name || 'Client'}'s Transaction`}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <StageBadge stage={file.stage} />
                      <span style={{ fontSize: '11px', color: colors.muted }}>Day {days}</span>
                      <span style={{ fontSize: '11px', color: colors.muted }}>•</span>
                      <span style={{ fontSize: '11px', color: colors.muted }}>
                        {file.client_name || 'Unknown Client'}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: colors.blue, fontWeight: 600 }}>
                      Next: Review documents
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Broker Reviews */}
        <div
          style={{
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderLeft: `4px solid ${colors.gold}`,
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: colors.navy }}>
              📋 Recent Broker Reviews
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {approvedCount > 0 && (
                <span
                  style={{
                    background: `${colors.green}22`,
                    color: colors.green,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                  }}
                >
                  {approvedCount} ✓
                </span>
              )}
              {pendingCount > 0 && (
                <span
                  style={{
                    background: `${colors.orange}22`,
                    color: colors.orange,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                  }}
                >
                  {pendingCount} ⏳
                </span>
              )}
              {revisionCount > 0 && (
                <span
                  style={{
                    background: `${colors.red}22`,
                    color: colors.red,
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                  }}
                >
                  {revisionCount} ⚠️
                </span>
              )}
            </div>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {landingReviews.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '13px' }}>
                No recent document reviews
              </div>
            ) : (
              landingReviews.map((review, i) => {
                const txn = transactions.find((t) => t.id === review.transaction_id);
                return (
                  <div
                    key={`${review.id}-${i}`}
                    onClick={() => txn && onSelectTransaction(txn)}
                    style={{
                      padding: '14px 16px',
                      borderBottom: i === landingReviews.length - 1 ? 'none' : `1px solid ${colors.border}`,
                      cursor: txn ? 'pointer' : 'default',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (txn) e.currentTarget.style.background = `${colors.gold}0a`;
                    }}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600, color: colors.navy, marginBottom: '2px' }}>
                      {review.document_name}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted, marginBottom: '6px' }}>
                      {review.property_address}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span
                        style={{
                          background:
                            review.status === 'approved'
                              ? `${colors.green}22`
                              : review.status === 'needs_revision'
                              ? `${colors.red}22`
                              : `${colors.orange}22`,
                          color:
                            review.status === 'approved'
                              ? colors.green
                              : review.status === 'needs_revision'
                              ? colors.red
                              : colors.orange,
                          fontSize: '10px',
                          fontWeight: 700,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {review.status === 'approved'
                          ? '✅ Approved'
                          : review.status === 'needs_revision'
                          ? '⚠️ Revision Needed'
                          : '⏳ Pending'}
                      </span>
                      {review.score != null && (
                        <span style={{ fontSize: '11px', color: colors.muted }}>
                          Score: {review.score}%
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: colors.muted, marginTop: '4px' }}>
                      {review.reviewed_date
                        ? `Reviewed ${formatAbsoluteDate(review.reviewed_date)}`
                        : `Submitted ${formatAbsoluteDate(review.submitted_date)}`}
                    </div>
                    {review.broker_notes && review.status !== 'approved' && (
                      <div
                        style={{
                          marginTop: '6px',
                          fontSize: '11px',
                          color: colors.red,
                          background: `${colors.red}0a`,
                          padding: '6px 8px',
                          borderRadius: '4px',
                          borderLeft: `2px solid ${colors.red}`,
                        }}
                      >
                        <strong>Note:</strong> {review.broker_notes}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// KPI card — single metric tile, used 5x in the landing dashboard.
// ---------------------------------------------------------------------------

const KpiCard: React.FC<{
  icon: any;
  iconColor: string;
  value: string;
  label: string;
}> = ({ icon, iconColor, value, label }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '10px',
      padding: '16px',
      textAlign: 'center',
      boxShadow: '0 2px 6px rgba(15, 30, 53, 0.04)',
    }}
  >
    <div
      style={{
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: `${iconColor}22`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 10px auto',
      }}
    >
      <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: '18px' }} />
    </div>
    <div style={{ fontFamily: fonts.display, fontSize: '26px', color: colors.navy, lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: '11px', color: colors.muted, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Landing helpers
// ---------------------------------------------------------------------------

// Extract city/state from a company address string. Legacy dashboard used
// the exact same lightweight parser — not a full address lib, just the
// last two comma-separated segments. Handles "123 Main St, Chandler, AZ 85286"
// and "Chandler, AZ" equally well.
//
// Declared as `function` (not `const`) so they're hoisted — the main
// AgentDocumentsHub component above calls these inside a useEffect, which
// TypeScript treats as a forward reference for const-arrow-functions.
function extractCityFromAddress(address?: string): string {
  if (!address) return '';
  const parts = address.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) return parts[parts.length - 2];
  if (parts.length === 2) return parts[0];
  return '';
}

function extractStateFromAddress(address?: string): string {
  if (!address) return '';
  const parts = address.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return '';
  // Last segment is typically "ST 85286" or just "AZ". Grab the first token.
  const last = parts[parts.length - 1];
  const token = last.split(' ')[0];
  return token.length === 2 ? token.toUpperCase() : '';
}

function formatCurrencyCompact(value: number | string | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value || 0;
  if (!n || isNaN(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatAbsoluteDate(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}

// ===========================================================================
// New Transaction Modal — minimum fields to POST /transactions. After
// creation the hub auto-selects the new tx and opens the Overview pane
// so the user can fill in the richer "Edit Transaction" fields there.
// ===========================================================================

function NewTransactionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  // Callback receives the new transaction id AND (if available) the
  // full transaction object from the POST response, so the parent can
  // optimistically prepend it to the list without waiting for refetch.
  onCreated: (newTxId: string, optimisticTx?: any) => void;
}) {
  const [form, setForm] = useState({
    type: 'purchase',
    client_name: '',
    client_email: '',
    client_phone: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_price: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const canSubmit = !!form.client_name.trim() && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const payload: any = {
        type: form.type,
        client_name: form.client_name,
      };
      if (form.client_email) payload.client_email = form.client_email;
      if (form.client_phone) payload.client_phone = form.client_phone;
      if (form.property_address) {
        payload.property_data = {
          address: form.property_address,
          city: form.property_city,
          state: form.property_state,
          zip: form.property_zip,
          price: form.property_price ? parseFloat(form.property_price.replace(/,/g, '')) : null,
        };
      }
      const res = await axiosInstance.post('/transactions', payload);
      if (res.data?.success && res.data?.transaction?.id) {
        // Pass the full transaction back so the parent can prepend it
        // to the list immediately (optimistic update).
        onCreated(res.data.transaction.id, res.data.transaction);
      } else {
        setError(res.data?.error || 'Failed to create transaction');
      }
    } catch (err: any) {
      if (err?.response?.data?.code === 'BROKERAGE_REQUIRED') {
        setError('You need to be associated with a brokerage to create transactions.');
      } else {
        setError(err?.response?.data?.error || err?.message || 'Failed to create transaction');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 30, 53, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.white,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          borderTop: `4px solid ${colors.gold}`,
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontFamily: fonts.display,
              fontSize: '22px',
              color: colors.navy,
              letterSpacing: '1px',
            }}
          >
            + New Transaction
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '22px',
              color: colors.muted,
              cursor: 'pointer',
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {error && (
            <div
              style={{
                background: `${colors.red}11`,
                border: `1px solid ${colors.red}66`,
                borderLeft: `3px solid ${colors.red}`,
                borderRadius: '6px',
                padding: '10px 14px',
                fontSize: '12px',
                color: colors.red,
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          <FormField label="Transaction Type">
            <Select
              value={form.type}
              onChange={(v) => update('type', v)}
              options={[
                { value: 'purchase', label: 'Purchase' },
                { value: 'listing', label: 'Listing' },
                { value: 'rental', label: 'Rental' },
                { value: 'referral', label: 'Referral' },
              ]}
            />
          </FormField>

          <div style={{ height: '12px' }} />

          <FormField label="Client Name *">
            <Input
              value={form.client_name}
              onChange={(v) => update('client_name', v)}
              placeholder="e.g., John Smith"
            />
          </FormField>

          <div style={{ height: '12px' }} />

          <FormGrid>
            <FormField label="Client Email">
              <Input
                type="email"
                value={form.client_email}
                onChange={(v) => update('client_email', v)}
                placeholder="client@example.com"
              />
            </FormField>
            <FormField label="Client Phone">
              <Input
                value={form.client_phone}
                onChange={(v) => update('client_phone', v)}
                placeholder="(555) 555-5555"
              />
            </FormField>
          </FormGrid>

          <div
            style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: `1px solid ${colors.border}`,
            }}
          >
            <FormField label="Property Address (optional — can add later)">
              <Input
                value={form.property_address}
                onChange={(v) => update('property_address', v)}
                placeholder="Street address"
              />
            </FormField>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: '10px',
                marginTop: '12px',
              }}
            >
              <FormField label="City">
                <Input
                  value={form.property_city}
                  onChange={(v) => update('property_city', v)}
                  placeholder="City"
                />
              </FormField>
              <FormField label="State">
                <Input
                  value={form.property_state}
                  onChange={(v) => update('property_state', v.toUpperCase().slice(0, 2))}
                  placeholder="ST"
                />
              </FormField>
              <FormField label="ZIP">
                <Input
                  value={form.property_zip}
                  onChange={(v) => update('property_zip', v)}
                  placeholder="ZIP"
                />
              </FormField>
            </div>

            <div style={{ marginTop: '12px', maxWidth: '260px' }}>
              <FormField label="Sale / List Price">
                <Input
                  prefix="$"
                  value={form.property_price}
                  onChange={(v) => update('property_price', v)}
                  placeholder="0.00"
                />
              </FormField>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '14px 24px',
            borderTop: `1px solid ${colors.border}`,
            background: colors.off,
            borderRadius: '0 0 12px 12px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
          }}
        >
          <OutlineButton onClick={onClose} disabled={saving}>
            Cancel
          </OutlineButton>
          <GoldButton onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? 'Creating…' : 'Create Transaction'}
          </GoldButton>
        </div>
      </div>
    </div>
  );
}

export default AgentDocumentsHub;
